// public/v3/src/pages/onboard.jsx — full-screen "Onboard patients" surface.
//
// Standalone roster for enrollment state. The primary list is unenrolled
// patients; the lower collapsible subsection exposes currently enrolled
// patients for admin unenrollment. Not gated by the active month (unlike Studio).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ccmApi } from '../lib/api';
import { suggestProgram, needsTriage, chronicCount } from '../lib/ai-suggest';
import { PANEL_REVIEW_SYSTEM_PROMPT, PANEL_REVIEW_USER_TEMPLATE } from '../lib/v2-prompts';
import {
  suggestionHash,
  buildSafeHarborPanelRow,
  parsePanelReviewResponse,
  chunkRows,
  pickCcmSuggestion,
  pickBhiSuggestion,
  normalizeCcmSuggestionForDisposition,
} from '../lib/panel-review';
import { suggestBhi } from '../lib/bhi-suggest';
import { clearConsentLinkProvenance } from '../lib/consent';
import { suggestPain } from '../lib/pain-suggest';
import { payerProgramBlocker, MEDICARE_CLASSES } from '../lib/payer-rules';
import { isEnrolledProgram, isUnenrolledCcm, isUnenrolledBhi, isUnenrolledPain, isUnenrolledPin, isUnenrolledFor, isActivePatient, isWorkflowExcluded } from '../lib/workflow';
import { EditPatientModal } from '../components/edit-patient-modal';
import { BhiConsentPill } from '../components/bhi-consent-pill';
import { ConsentLinksPanel, ConsentFreshLink } from '../components/consent-links-panel';
import { createConsentLink, listConsentLinks } from '../lib/consent-link-api';
import { PCM_BASE_CODES, pcmConditionText } from '../lib/pcm-principal';
import { latestConsentLinkByPatient, consentLinkBadge } from '../lib/consent-link-status';
import { auditConsentChange, auditBhiConsentChange, auditPainConsentChange, auditPinConsentChange, safeAudit } from '../lib/consent-audit';
import { confirmDialog } from '../components/confirm-dialog';
import { CPM_BILLING_CREDENTIALS, buildPainNecessityDraft, buildPainEnrollmentRecord, isPainRecordComplete } from '../lib/pain-billing';
import { buildPinEnrollmentRecord, isPinRecordComplete } from '../lib/pin-billing';

function fullName(raw) {
  return `${raw.fname || ''} ${raw.lname || ''}`.trim() || raw.name || raw.mrn || '';
}
function nameInitials(raw) {
  return fullName(raw).split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
}
function avSlotFor(id) {
  const tail = String(id || '').slice(-2);
  const n = parseInt(tail, 16);
  if (!Number.isFinite(n)) return 1;
  return (n % 6) + 1;
}
function ageFromDob(dob) {
  if (!dob) return null;
  const t = Date.parse(dob);
  if (!Number.isFinite(t)) return null;
  const yrs = (Date.now() - t) / (365.2425 * 24 * 3600 * 1000);
  if (yrs < 0 || yrs > 130) return null;
  return Math.floor(yrs);
}

// Adapter: flatten row → { id, raw, name, mrn, age, conditions, program, consent, chronic, suggestion, needs }
function buildLocalBhiSuggestionPayload(bhiSuggestion) {
  if (!bhiSuggestion || !bhiSuggestion.eligible) return null;
  const code = String(bhiSuggestion.recommendedCode || '99484').trim() || '99484';
  const scale = bhiSuggestion.suggestedScale || 'PHQ-9';
  const reasons = Array.isArray(bhiSuggestion.reasons) ? bhiSuggestion.reasons.filter(Boolean) : [];
  const reason = bhiSuggestion.reason || reasons.join('; ') || 'behavioral-health signal';
  return {
    programFamily: 'BHI',
    recommendedCode: code,
    recommendedLabel: `BHI ${code}`,
    confidence: 'rule',
    reason,
    reasons,
    suggestedScale: scale,
    missingValidation: [],
    source: 'local_heuristic',
  };
}

// Onboard displays CCM tier-agnostically: the subprogram (99487 complex /
// 99491 provider-led) is a MONTHLY staff decision made where the time lives
// (Time Logs milestone bars + the Studio tier-bump gate), never an
// enrollment-time choice — every accept lands at the 99490 base anyway. The
// AI's tier rationale stays visible in the suggestion reason/evidence, not
// in the action label.
function formatCcmSuggestionLabel(suggestion) {
  const code = String(suggestion?.program || suggestion?.recommendedCode || '').trim();
  if (code === '99491' || code === '99487' || code === '99490') return 'CCM';

  const label = String(suggestion?.label || suggestion?.recommendedLabel || code || '').trim();
  if (!label) return 'CCM';
  if (/\b(99490|99491|99487)\b/.test(label)) return 'CCM';
  if (/\bccm\b/i.test(label)) return label;
  return `CCM ${label}`;
}

function formatBhiSuggestionLabel(suggestion) {
  const code = String(suggestion?.recommendedCode || suggestion?.program || '99484').trim() || '99484';
  return `BHI ${code}`;
}

// Row-badge labels for the derived payer class (classes from payer-rules.js).
const PAYER_LABEL = {
  medicare: 'Medicare', ma: 'MA', dual: 'Dual',
  medicaid: 'Medicaid', commercial: 'Commercial', unknown: 'Payer?',
};

// Carrier bucket for the per-payer sub-lens under the Commercial insurance
// lens (primaryCarrier = insurances[0].carrier, primary-first per the mapper).
const NO_CARRIER_LABEL = 'No carrier on file';
function carrierBucket(p) {
  return String(p.primaryCarrier || '').trim() || NO_CARRIER_LABEL;
}

function adapt(row, bhiSettings = {}) {
  if (!row || typeof row !== 'object') return null;
  const data = row.data && typeof row.data === 'object' ? row.data : row;
  const program = (data.billing && data.billing.program) || '';
  const bhiSuggestion = suggestBhi(data, bhiSettings || {});
  const painSuggestion = suggestPain(data);
  const consent = data.consent || 'pending';
  return {
    id: row.id,
    raw: data,
    name: fullName(data),
    mrn: data.mrn || '',
    age: ageFromDob(data.dob),
    sex: data.sex || '',
    program: program || 'none',
    consent,
    payerClass: String(data.payerClass || 'unknown').toLowerCase(),
    primaryCarrier: (Array.isArray(data.insurances) && data.insurances[0] && data.insurances[0].carrier) || '',
    active: isActivePatient(data),
    workflowExcluded: isWorkflowExcluded(data),
    chronic: chronicCount({ _raw: data }),
    suggestion: suggestProgram({ _raw: data }),
    bhiSuggestion,
    painSuggestion,
    localBhiSuggestion: buildLocalBhiSuggestionPayload(bhiSuggestion),
    needs: needsTriage({ _raw: data }),
    tags: Array.isArray(data.tags) ? data.tags.filter(Boolean).map(String) : [],
    avSlot: avSlotFor(row.id),
  };
}

// "Unenrolled" for onboarding purposes: no real program set. Backed by
// isEnrolledProgram from lib/workflow.js so v3 has one source of truth for
// program normalization (both '' and 'none' resolve to "not enrolled").
//
// v3 filters Onboard to active + not declined + not Elation-workflow-excluded
// patients by default (matches v2's getActivePatients minus the month-cutoff
// date check); the `showDeclined` toggle reveals declined patients only.
// Workflow-excluded patients (Elation-marked deliberately out-of-workflow)
// have no comparable toggle — they stay hidden.
// Program-aware decline predicate. CCM decline lives on the patient's CCM
// consent state; BHI decline lives on patient.raw.bhi.declined (Phase 4.5+
// bulk-decline path). 'all' falls back to CCM-decline so the All view keeps
// today's "Show declined" behavior unchanged.
function isDeclinedFor(p, programFilter) {
  if (programFilter === 'bhi') return !!(p && p.raw && p.raw.bhi && p.raw.bhi.declined);
  return p && p.consent === 'declined';
}

// CCM tier codes normalize to the 99490 base at enrollment: the billed tier
// is a MONTHLY decision (effectiveCcmCodeForMonth — >=90 min + operator
// MDM/physician-time affirmation via the tier-bump flow), not an enrollment
// property. Enrolling at 99491/99487 would hard-code a tier the month's
// documentation may not support — and the bump flow only watches 99490
// enrollees. PCM codes (99424/99426) pass through untouched: PCM is a
// different program (single principal condition), not a CCM tier.
const CCM_TIER_ENROLL_NORMALIZE = { '99491': '99490', '99487': '99490' };

// Consent/billing attestation copy — one place, so the legal wording stays
// identical across every enrollment entry point (bulk, row, AI-apply).
const ATTEST_VERBAL = 'Attests verbal consent obtained. Audit-logged.';
const ATTEST_VERBAL_EACH = 'Attests verbal consent obtained for each. Audit-logged.';
const PAIN_ATTEST_BULLETS = [
  'An in-person, face-to-face initiating visit of at least 30 minutes was performed by the billing practitioner.',
  'Monthly CPM time will be practitioner/QHP time (G3002 is not a clinical-staff code).',
];
// Row-level Pain confirm keeps its own (compliance-approved) wording — it is
// deliberately NOT unified with the bulk copy above.
const PAIN_ATTEST_BULLETS_ROW = [
  'An in-person, face-to-face initiating visit of at least 30 minutes was performed by the billing practitioner.',
  'Monthly CPM time will be practitioner/QHP time.',
];
// CPM patient disclosures — shown as bullets in the enroll dialog; the operator
// attests (audit-logged) they were made, and pain.consent.disclosures records it.
const PAIN_DISCLOSURE_BULLETS = [
  'Patient informed that Medicare coinsurance applies.',
  'Patient informed of the right to discontinue services at any time.',
];
const PIN_ATTEST_BULLETS_ROW = [
  'Patient consented to Principal Illness Navigation services (verbal consent recorded).',
  'The patient has a serious, high-risk condition expected to last 3 months or longer (recorded below).',
  'An initiating visit with the billing practitioner occurred on the date recorded below.',
  'Navigation is furnished by trained auxiliary personnel under the general supervision of the billing practitioner; training documentation is on file.',
];
const PIN_DISCLOSURE_BULLETS = [
  'Patient informed that Medicare coinsurance/cost-sharing may apply.',
  'Patient informed of the right to discontinue services at any time.',
  'Patient informed that only one practitioner may bill PIN per period.',
];
// HARD eligibility attestation (plan P0.2) — the confirm click attests it and
// pain.consent.durationOver3Months persists it. Never prefilled/defaulted:
// prefill facts, never attestations.
const PAIN_DURATION_BULLET = 'Chronic pain has persisted, or is expected to persist, longer than 3 months.';

// Staff eligible to be the DESIGNATED CPM billing provider — credential must be
// in the CMS CPM-authorized set (MD/DO/NP/PA/CNM). The membership test is
// IDENTICAL to the evidence counter's (pain-billing.js) so a provider who can
// be selected here is one whose logged time will actually count. Returns select
// options plus a byId map that resolves the chosen id back to a name.
async function loadCpmProviderOptions(currentUser) {
  const list = await ccmApi.listStaff();
  const eligible = (Array.isArray(list) ? list : []).filter((s) => s && CPM_BILLING_CREDENTIALS.has(s.credential));
  const options = eligible.map((s) => ({
    value: String(s.id),
    label: s.credential ? `${s.name || s.id} (${s.credential})` : String(s.name || s.id),
  }));
  const byId = {};
  eligible.forEach((s) => { byId[String(s.id)] = s; });
  // CPM is provider-self-service: the enrolling provider is almost always the
  // designated biller, so default the select to THEIR eligible staff record —
  // stable-id match first, unique name match second (same resolution order as
  // the time-log credential stamp). Role never matters here: an admin whose
  // staff record carries a provider credential resolves like any provider.
  let selfId = '';
  if (currentUser) {
    const byUid = eligible.find((s) => currentUser.id != null && String(s.id) === String(currentUser.id));
    if (byUid) selfId = String(byUid.id);
    else {
      const uname = String(currentUser.displayName || currentUser.name || currentUser.email || '').trim().toLowerCase();
      const byName = uname ? eligible.filter((s) => String(s.name || '').trim().toLowerCase() === uname) : [];
      if (byName.length === 1) selfId = String(byName[0].id);
    }
  }
  return { options, byId, selfId };
}

function applyEnrollment(raw, program, consentDate) {
  const normalized = CCM_TIER_ENROLL_NORMALIZE[program] || program;
  raw.billing = { ...(raw.billing || {}), program: normalized };
  delete raw.ccmUnenrollment;
  if (raw.consent !== 'signed') {
    raw.consent = 'signed';
    raw.consentDate = consentDate;
    clearConsentLinkProvenance(raw);
  }
}

// BHI counterpart to applyEnrollment. Sets billing.bhiProgram, stamps
// bhi.enrolledAt (clears any prior unenroll markers), and atomically
// captures verbal BHI consent if not already on file. Operators who click
// Accept are attesting that verbal consent was obtained — same model as
// CCM. Use BhiConsentPill on the row to upgrade verbal → written later.
function applyBhiEnrollment(raw, code, consentDate) {
  raw.billing = { ...(raw.billing || {}), bhiProgram: code };
  const priorBhi = raw.bhi || {};
  raw.bhi = { ...priorBhi, enrolledAt: new Date().toISOString() };
  delete raw.bhi.unenrolledAt;
  delete raw.bhi.unenrolledReason;
  if (!raw.bhiConsent || raw.bhiConsent.signed !== true) {
    raw.bhiConsent = { signed: true, date: consentDate, source: 'verbal' };
  }
}

function dispositionStillNeedsEnrollment(p, slot, disp) {
  if (!disp || disp.status !== 'implemented') return false;
  if (slot === 'bhi') return isUnenrolledBhi(p);
  return isUnenrolledCcm(p);
}

function shouldShowDisposition(p, slot, disp) {
  if (!disp) return false;
  return !dispositionStillNeedsEnrollment(p, slot, disp);
}

function dispositionStatusLabel(status) {
  if (status === 'implemented') return 'Accepted';
  if (status === 'dismissed') return 'Dismissed';
  if (status === 'sent_to_provider') return 'Sent to provider';
  return String(status || '').replace(/_/g, ' ');
}

function buildUnenrolledRaw(p, month, reason, programSlot = 'ccm') {
  const raw = { ...p.raw };
  const date = new Date().toISOString().slice(0, 10);
  const cleanReason = String(reason || '').trim();

  if (programSlot === 'bhi') {
    // BHI unenroll: clear billing.bhiProgram and stamp bhi.unenrolledAt /
    // unenrolledReason. CCM program + apcmMonth state are left untouched —
    // a patient unenrolled from BHI can still be enrolled in CCM.
    const priorBilling = raw.billing || {};
    const nextBilling = { ...priorBilling };
    delete nextBilling.bhiProgram;
    raw.billing = nextBilling;
    const priorBhi = raw.bhi || {};
    raw.bhi = {
      ...priorBhi,
      unenrolledAt: new Date().toISOString(),
      unenrolledReason: cleanReason,
    };
    return raw;
  }

  // CCM unenroll (existing behavior, unchanged):
  const previousProgram = (raw.billing && raw.billing.program) || p.program || '';
  const nextBilling = { ...(raw.billing || {}), program: 'none' };
  delete nextBilling.apcmAutoFallback;
  raw.billing = nextBilling;

  if (raw.apcmMonth && typeof raw.apcmMonth === 'object' && month) {
    const nextApcm = { ...raw.apcmMonth };
    delete nextApcm[month];
    if (Object.keys(nextApcm).length) raw.apcmMonth = nextApcm;
    else delete raw.apcmMonth;
  }

  const entry = {
    action: 'unenrolled',
    date,
    reason: cleanReason,
    previousProgram,
    source: 'v3-onboard',
  };
  raw.ccmUnenrollment = entry;
  raw.ccmEnrollmentHistory = [
    ...(Array.isArray(raw.ccmEnrollmentHistory) ? raw.ccmEnrollmentHistory : []),
    entry,
  ].slice(-20);
  return raw;
}

// `hiddenOnboardTags` is retired (tag chips were replaced by insurance
// lenses); the prop is kept so existing call sites don't break.
export function OnboardPage({ month, onBadgeChange, hideNoChronic, hiddenOnboardTags = [], programFilter = 'ccm', bhiEnabled = false, painEnabled = false, pinEnabled = false, currentUser = null }) {
  const [patients, setPatients] = useState([]);
  // dispositions: patient_id → { ccm: dispRow|null, bhi: dispRow|null }. A
  // patient may now have up to two pending dispositions per review month
  // (one CCM-slot, one BHI-slot) so the UI tracks both alongside each row.
  const [dispositions, setDispositions] = useState({});
  // `combined_care_mgmt_consent` setting: when true, a signed CCM consent
  // automatically covers BHI (mirrors APCM-fallback's inheritance rule). The
  // row renders an "inherited" badge instead of <BhiConsentPill> in that case.
  const [combinedConsent, setCombinedConsent] = useState(false);
  // consent_links_enabled setting: shows the Consent-links panel and the
  // per-row "Send consent link" action (the secondary, patient-approved
  // consent path). Off by default — opt-in rollout per practice.
  const [consentLinksEnabled, setConsentLinksEnabled] = useState(false);
  const [consentLinksOpen, setConsentLinksOpen] = useState(false);
  const [consentFresh, setConsentFresh] = useState(null); // inline row mint result
  const [consentReloadKey, setConsentReloadKey] = useState(0);
  // patientId → most recent consent link (for the per-row status badge).
  const [consentLinkMap, setConsentLinkMap] = useState({});
  // Consent lens chips: null (off) | 'pending' | 'declined'. The declined lens
  // shows ONLY declined rows (they're hidden by default), giving declined
  // patients a first-class surface instead of a buried toggle.
  const [consentLens, setConsentLens] = useState(null);
  // Elation-workflow-excluded patients are hidden by default; this reveals
  // them (with an "Excluded" badge) so they're no longer permanently invisible.
  const [showExcluded, setShowExcluded] = useState(false);
  const [reofferPatient, setReofferPatient] = useState(null); // ReofferConsentModal target
  // PCM enrollment condition assignment: { program, rows: [{ p, options, choice }] }.
  // PCM manages exactly ONE condition — the choice is captured here at enroll
  // time and persisted to billing.pcmCondition (see bulkEnrollPcm).
  const [pcmAssign, setPcmAssign] = useState(null);
  const [bhiSettings, setBhiSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [showDeclined, setShowDeclined] = useState(false);
  // CCM requires ≥2 chronic conditions per CMS. Hidden by default so the list
  // opens focused on actually-eligible candidates; user can reveal via toggle.
  const [showBelowThreshold, setShowBelowThreshold] = useState(!hideNoChronic);
  // Insurance lenses — replace the retired Elation-tag chips (tag flow runs
  // the other way now: the app auto-tags Elation on enrollment).
  // null | 'medicare' (FFS + MA + dual) | 'commercial' | 'medicaid' | 'other'
  const [payerLens, setPayerLens] = useState(null);
  // Per-carrier sub-lens, only offered while the Commercial lens is active —
  // commercial CCM coverage is carrier-by-carrier (Anthem/UHC/Emblem publish
  // policies; Aetna/Cigna are pilot-and-read-the-EOB), so staff work payer
  // pilots as one chip each. null = all carriers.
  const [carrierLens, setCarrierLens] = useState(null);
  const [age65, setAge65] = useState(false); // 65+ age lens; composes (AND) with the payer lens
  const [selected, setSelected] = useState(new Set());
  const [selectedEnrolled, setSelectedEnrolled] = useState(new Set());
  const [enrolledOpen, setEnrolledOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [unenrollOpen, setUnenrollOpen] = useState(null);
  const [editPatient, setEditPatient] = useState(null);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [tierMenuOpen, setTierMenuOpen] = useState(false);
  const tierMenuRef = useRef(null);
  // Anchor for shift+click range selection. Index into `visible`. Reset when
  // the surface closes or the visible list changes such that the index would
  // refer to a different row.
  const rangeAnchorRef = useRef(null);

  // Reload on open so a freshly-synced patient appears without a hard refresh.
  // Settings load drives the combined-consent inheritance badge for BHI; falls
  // back to "off" silently when the settings endpoint is unavailable so a
  // failed settings GET doesn't block the rest of the page.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      ccmApi.listPatients(),
      ccmApi.listDispositions(month).catch(() => ({ dispositions: [] })),
      ccmApi.getSettings().catch(() => ({})),
    ]).then(([rows, dispRes, settings]) => {
      if (cancelled) return;
      // Bucket dispositions by (patient_id, program_slot). Within a slot, keep
      // the most recent row. CCM and BHI live side-by-side per patient.
      const byPatient = {};
      for (const d of (dispRes.dispositions || [])) {
        const slot = (d.program_slot || '').toLowerCase();
        if (slot !== 'ccm' && slot !== 'bhi') continue;
        if (!byPatient[d.patient_id]) byPatient[d.patient_id] = { ccm: null, bhi: null };
        const prior = byPatient[d.patient_id][slot];
        if (!prior || new Date(d.updated_at) > new Date(prior.updated_at)) {
          byPatient[d.patient_id][slot] = d;
        }
      }
      setDispositions(byPatient);
      setBhiSettings(settings || {});
      const v = settings?.combined_care_mgmt_consent;
      setCombinedConsent(v === true || v === 'true');
      const cl = settings?.consent_links_enabled;
      setConsentLinksEnabled(cl === true || cl === 'true');
      setPatients((rows || []).map(row => adapt(row, settings || {})).filter(Boolean));
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      setError(e?.message || 'Failed to load patients');
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [month]);

  // Settings owns the default threshold posture. Keep this mounted tab in
  // sync when the preference changes, while preserving the master hide-zero
  // gate in the filters below.
  useEffect(() => {
    setShowBelowThreshold(!hideNoChronic);
  }, [hideNoChronic]);

  // ESC closes the tier menu when it's open. The tab itself stays mounted.
  useEffect(() => {
    if (!tierMenuOpen) return;
    const h = (e) => { if (e.key === 'Escape') setTierMenuOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [tierMenuOpen]);

  // Click-outside closes the tier menu. Attached only while the menu is open.
  useEffect(() => {
    if (!tierMenuOpen) return;
    const h = (e) => {
      if (tierMenuRef.current && !tierMenuRef.current.contains(e.target)) {
        setTierMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [tierMenuOpen]);

  function isBhiSuggestedCandidate(p) {
    return !!(bhiEnabled && (programFilter === 'bhi' || programFilter === 'all') && p?.bhiSuggestion?.eligible);
  }

  // Pain candidacy is pain-lens-only (CPM is a concurrent add-on for the
  // existing panel — surfacing it in All would noise up CCM onboarding).
  function isPainSuggestedCandidate(p) {
    return !!(painEnabled && programFilter === 'pain' && p?.painSuggestion?.eligible);
  }

  // Mirrors nav-badge-counts.passesOnboardThreshold: each onboarding path
  // leads to a concrete billed code, so candidacy requires the payer class to
  // reimburse it (Medicaid-only admits stay out of the CCM queue; commercial
  // patients — whose plans pay CCM CPTs — remain onboardable).
  function passesVisibleConditionThreshold(p) {
    const payerClass = p.raw?.payerClass || '';
    if (programFilter === 'pain') {
      // Pain lens: candidacy is the chart's pain signal + a payer that
      // reimburses G3002 (Medicare-only G-code).
      return isPainSuggestedCandidate(p) && !payerProgramBlocker(payerClass, 'G3002');
    }
    if (programFilter === 'pin') {
      return !!(pinEnabled && p.chronic >= 1) && !payerProgramBlocker(payerClass, 'G0023');
    }
    const ccmOk = p.chronic >= 2 && !payerProgramBlocker(payerClass, '99490');
    const bhiOk = isBhiSuggestedCandidate(p) && !payerProgramBlocker(payerClass, '99484');
    return ccmOk || bhiOk;
  }

  // Report the default-visible count (active + non-workflow-excluded +
  // unenrolled + meets the active program's condition threshold + not declined)
  // up to the chrome so the "Onboard" tab can render a count badge. BHI does
  // not require two chronic conditions, so a local BHI signal keeps the row
  // visible even when the CCM chronic threshold is not met.
  useEffect(() => {
    if (!onBadgeChange) return;
    const n = patients.filter((p) =>
      p.active && !p.workflowExcluded &&
      isUnenrolledFor(p, programFilter) && !isDeclinedFor(p, programFilter) && passesVisibleConditionThreshold(p)
    ).length;
    onBadgeChange(n);
  }, [patients, onBadgeChange, programFilter, bhiEnabled, pinEnabled]);

  // Insurance-lens membership. 'other' = anything not covered by the three
  // named lenses (unknown/unclassified payers).
  function payerLensMatch(cls, lens) {
    if (!lens) return true;
    if (lens === 'medicare') return MEDICARE_CLASSES.has(cls);
    if (lens === 'commercial') return cls === 'commercial';
    if (lens === 'medicaid') return cls === 'medicaid';
    return !MEDICARE_CLASSES.has(cls) && cls !== 'commercial' && cls !== 'medicaid';
  }

  // The carrier sub-lens only exists under Commercial — leaving that lens
  // with one active would silently keep narrowing the roster, so reset.
  useEffect(() => {
    if (payerLens !== 'commercial') setCarrierLens(null);
  }, [payerLens]);

  // When the chrome program filter changes, drop any pending row selection.
  // A row visible in CCM view may not be in BHI view (different list slice),
  // and bulk actions are program-specific — a stale selection across a switch
  // could accidentally bulk-enroll the wrong patients in the wrong program.
  useEffect(() => {
    setSelected(new Set());
    setSelectedEnrolled(new Set());
  }, [programFilter]);

  // Consent-link status per patient, for the row badges. Refreshes alongside
  // the panel (consentReloadKey bumps after an inline mint). Silent failure —
  // a missing badge must not block the roster.
  useEffect(() => {
    if (!consentLinksEnabled) { setConsentLinkMap({}); return; }
    let cancelled = false;
    listConsentLinks()
      .then((rows) => { if (!cancelled) setConsentLinkMap(latestConsentLinkByPatient(rows)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [consentLinksEnabled, consentReloadKey, month]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return patients.filter(p => {
      if (!p.active) return false;
      if (p.workflowExcluded && !showExcluded) return false;
      if (hideNoChronic && p.chronic === 0 && !isBhiSuggestedCandidate(p)) return false;
      if (!isUnenrolledFor(p, programFilter)) return false;
      // Consent lens: 'declined' shows ONLY declined rows (overriding the
      // default hide); 'pending' narrows to CCM-consent-pending rows.
      if (consentLens === 'declined') {
        if (!isDeclinedFor(p, programFilter)) return false;
      } else if (!showDeclined && isDeclinedFor(p, programFilter)) return false;
      if (consentLens === 'pending' && (p.consent !== 'pending' || isDeclinedFor(p, programFilter))) return false;
      if (!showBelowThreshold && !passesVisibleConditionThreshold(p)) return false;
      if (!payerLensMatch(p.payerClass, payerLens)) return false;
      if (payerLens === 'commercial' && carrierLens && carrierBucket(p) !== carrierLens) return false;
      if (age65 && !(p.age >= 65)) return false;
      if (q) {
        const hay = `${p.name} ${p.mrn}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [patients, query, showDeclined, showBelowThreshold, payerLens, carrierLens, age65, hideNoChronic, programFilter, bhiEnabled, pinEnabled, consentLens, showExcluded]);

  const enrolledVisible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return patients.filter(p => {
      if (!p.active) return false;
      if (p.workflowExcluded && !showExcluded) return false;
      if (hideNoChronic && p.chronic === 0 && !isBhiSuggestedCandidate(p)) return false;
      // Enrolled subsection pivots per program: CCM/All view = patients with
      // billing.program set (today's behavior); BHI view = patients with
      // billing.bhiProgram set; Pain/PIN views = patients with their dedicated
      // concurrent-program enrollment set. Without this, an enrollee with no
      // CCM base program is in neither list, so an incomplete-record chip could
      // never be reached or completed.
      if (programFilter === 'bhi') {
        if (isUnenrolledBhi(p)) return false;
      } else if (programFilter === 'pain') {
        if (isUnenrolledPain(p)) return false;
      } else if (programFilter === 'pin') {
        if (isUnenrolledPin(p)) return false;
      } else {
        if (!isEnrolledProgram(p.program)) return false;
      }
      if (!showDeclined && isDeclinedFor(p, programFilter)) return false;
      // Dedicated-program enrollees must stay reachable in their own lens even
      // after a live candidacy signal disappears; otherwise the incomplete-record
      // backfill action is hidden. Scope the bypass to enrolled Pain/PIN rows.
      const painEnrolledRow = programFilter === 'pain' && !isUnenrolledPain(p);
      const pinEnrolledRow = programFilter === 'pin' && !isUnenrolledPin(p);
      if (!showBelowThreshold && !painEnrolledRow && !pinEnrolledRow && !passesVisibleConditionThreshold(p)) return false;
      if (!payerLensMatch(p.payerClass, payerLens)) return false;
      if (payerLens === 'commercial' && carrierLens && carrierBucket(p) !== carrierLens) return false;
      if (age65 && !(p.age >= 65)) return false;
      if (q) {
        const hay = `${p.name} ${p.mrn}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [patients, query, showDeclined, showBelowThreshold, payerLens, carrierLens, age65, hideNoChronic, programFilter, bhiEnabled, pinEnabled, showExcluded]);

  const visibleWithAI = useMemo(() => {
    return visible.map((p) => {
      const byslot = dispositions[p.id];
      if (!byslot) return p;
      const proj = { ...p };
      if (shouldShowDisposition(p, 'ccm', byslot.ccm)) {
        proj._aiReview = {
          status: byslot.ccm.status,
          suggestion_payload: byslot.ccm.suggestion_payload || null,
          suggestion_hash: byslot.ccm.suggestion_hash,
          updated_at: byslot.ccm.updated_at,
        };
      }
      if (shouldShowDisposition(p, 'bhi', byslot.bhi)) {
        proj._aiBhiReview = {
          status: byslot.bhi.status,
          suggestion_payload: byslot.bhi.suggestion_payload || null,
          suggestion_hash: byslot.bhi.suggestion_hash,
          updated_at: byslot.bhi.updated_at,
        };
      }
      return proj;
    });
  }, [visible, dispositions]);

  // Tag chips: sorted unique set of tags across the reachable onboarding
  // surface. Per-chip counts mirror the `counts` memo gate so
  // the numbers reflect what the chip would surface if toggled on. Tags are
  // case-preserved for display but compared case-insensitively in the filter.
  const payerCounts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = patients.filter(p => {
      if (!p.active) return false;
      if (p.workflowExcluded && !showExcluded) return false;
      if (hideNoChronic && p.chronic === 0 && !isBhiSuggestedCandidate(p)) return false;
      if (!showDeclined && isDeclinedFor(p, programFilter)) return false;
      if (!showBelowThreshold && !passesVisibleConditionThreshold(p)) return false;
      if (q) {
        const hay = `${p.name} ${p.mrn}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const counts = { medicare: 0, commercial: 0, medicaid: 0, other: 0, age65: 0 };
    for (const p of pool) {
      if (MEDICARE_CLASSES.has(p.payerClass)) counts.medicare++;
      else if (p.payerClass === 'commercial') counts.commercial++;
      else if (p.payerClass === 'medicaid') counts.medicaid++;
      else counts.other++;
      if (p.age >= 65) counts.age65++;
    }
    return counts;
  }, [patients, query, showDeclined, showBelowThreshold, hideNoChronic, programFilter, bhiEnabled, pinEnabled, showExcluded]);

  // Per-carrier counts for the Commercial sub-lens chips — over the same pool
  // the payer chips count (so the carrier numbers sum to the Commercial chip),
  // narrowed to the commercial class and the active 65+ toggle. Sorted by
  // count, then name; null while the Commercial lens is off.
  const carrierCounts = useMemo(() => {
    if (payerLens !== 'commercial') return null;
    const q = query.trim().toLowerCase();
    const counts = new Map();
    for (const p of patients) {
      if (!p.active) continue;
      if (p.workflowExcluded && !showExcluded) continue;
      if (hideNoChronic && p.chronic === 0 && !isBhiSuggestedCandidate(p)) continue;
      if (!showDeclined && isDeclinedFor(p, programFilter)) continue;
      if (!showBelowThreshold && !passesVisibleConditionThreshold(p)) continue;
      if (p.payerClass !== 'commercial') continue;
      if (age65 && !(p.age >= 65)) continue;
      if (q) {
        const hay = `${p.name} ${p.mrn}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      const key = carrierBucket(p);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [payerLens, patients, query, showDeclined, showBelowThreshold, hideNoChronic, programFilter, bhiEnabled, pinEnabled, showExcluded, age65]);

  // Counts for the consent lens chips, over the same reachable unenrolled pool
  // `visible` draws from (before the lens itself narrows it). Declined rows
  // count as declined even though they're hidden by default — the chip is how
  // staff discover them.
  const consentCounts = useMemo(() => {
    let pending = 0, declined = 0;
    for (const p of patients) {
      if (!p.active) continue;
      if (p.workflowExcluded && !showExcluded) continue;
      if (hideNoChronic && p.chronic === 0 && !isBhiSuggestedCandidate(p)) continue;
      if (!isUnenrolledFor(p, programFilter)) continue;
      if (!showBelowThreshold && !passesVisibleConditionThreshold(p)) continue;
      if (isDeclinedFor(p, programFilter)) { declined++; continue; }
      if (p.consent === 'pending') pending++;
    }
    return { pending, declined };
  }, [patients, showExcluded, hideNoChronic, showBelowThreshold, programFilter, bhiEnabled, pinEnabled]);

  const allSelected = visible.length > 0 && visible.every(p => selected.has(p.id));
  const selPatients = visible.filter(p => selected.has(p.id));
  const selectedCount = selPatients.length;
  const selectedRuleSuggestionCount = useMemo(() => {
    return selPatients.reduce((n, p) => {
      const { ccmSugg, bhiSugg } = ruleSuggestionTargetsFor(p);
      return n + (ccmSugg ? 1 : 0) + (bhiSugg ? 1 : 0);
    }, 0);
  }, [selPatients, dispositions, programFilter]);
  const selectedLlmSuggestionCount = useMemo(() => {
    return selPatients.reduce((n, p) => {
      const { ccmSugg, bhiSugg } = pendingLlmTargetsFor(p);
      return n + (ccmSugg ? 1 : 0) + (bhiSugg ? 1 : 0);
    }, 0);
  }, [selPatients, dispositions, programFilter]);
  const allEnrolledSelected = enrolledVisible.length > 0 && enrolledVisible.every(p => selectedEnrolled.has(p.id));
  const selEnrolledPatients = enrolledVisible.filter(p => selectedEnrolled.has(p.id));

  function toggle(id, idx) {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
    if (typeof idx === 'number') rangeAnchorRef.current = idx;
  }
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visible.map(p => p.id)));
    rangeAnchorRef.current = null;
  }
  function toggleEnrolled(id) {
    const n = new Set(selectedEnrolled);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelectedEnrolled(n);
  }
  function toggleAllEnrolled() {
    if (allEnrolledSelected) setSelectedEnrolled(new Set());
    else setSelectedEnrolled(new Set(enrolledVisible.map(p => p.id)));
  }
  // Row-body click: plain click toggles, shift+click adds the range from the
  // anchor to the current row (Finder/Explorer convention). Anchor is the
  // last single-clicked row; shift-clicks don't move it.
  function rowClick(e, idx) {
    const p = visible[idx];
    if (!p) return;
    if (e.shiftKey && rangeAnchorRef.current !== null && rangeAnchorRef.current !== idx) {
      const a = rangeAnchorRef.current;
      const [lo, hi] = a <= idx ? [a, idx] : [idx, a];
      const next = new Set(selected);
      for (let i = lo; i <= hi; i++) {
        if (visible[i]) next.add(visible[i].id);
      }
      setSelected(next);
    } else {
      toggle(p.id, idx);
    }
  }
  function flashToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }

  // After any mutation, reload the patient list so derived rows + suggestions
  // reflect the new state. Kept simple — onboarding is a low-frequency op so
  // a full refetch is fine.
  async function refresh() {
    try {
      const rows = await ccmApi.listPatients();
      setPatients((rows || []).map(row => adapt(row, bhiSettings || {})).filter(Boolean));
    } catch (e) {
      setError(e?.message || 'Refresh failed');
    }
  }

  // Bulk: write program + (optionally) consent='signed' with first-visit date
  // pulled from Elation when available. Mirrors Triage's enrollAndConsent.
  //
  // Each PUT body carries p.raw._updatedAt so the server's 409 optimistic-lock
  // fires on stale writes; on 409 we surface a soft toast and refetch the
  // whole list so the operator can retry against fresh state. p.raw is `data`
  // from adapt() and preserves _updatedAt as injected by GET /data/patients
  // (server/routes/patients.js:85).
  async function bulkEnroll(program) {
    if (selPatients.length === 0) return;
    // PCM is disease-specific: route through the condition-assignment modal so
    // every enrollment pins its principal condition (billing.pcmCondition).
    if (PCM_BASE_CODES.has(program)) return bulkEnrollPcm(program);
    const n = selPatients.length;
    if (!(await confirmDialog({
      title: `Enroll ${n} patient${n !== 1 ? 's' : ''} in CCM ${program}?`,
      bullets: [ATTEST_VERBAL_EACH],
      confirmLabel: 'Enroll',
    }))) return;
    setBusy(true); setError(null);
    try {
      const elationIds = selPatients
        .map(p => p.raw.elation_id ? String(p.raw.elation_id) : null)
        .filter(Boolean);
      let dates = {};
      if (elationIds.length) {
        try { dates = (await ccmApi.firstVisitDates(elationIds)).results || {}; }
        catch (_) { dates = {}; }
      }
      const today = new Date().toISOString().slice(0, 10);
      let conflicts = 0;
      const results = await Promise.allSettled(selPatients.map(async (p) => {
        const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
        const consentDate = (elid && dates[elid]) || today;
        const prevConsent = p.raw.consent || p.consent || 'pending';
        const raw = { ...p.raw };
        applyEnrollment(raw, program, consentDate);
        await ccmApi.updatePatient(p.id, raw);
        if (prevConsent !== 'signed' && raw.consent === 'signed') {
          await auditConsentChange({ ...p, _raw: p.raw }, prevConsent, 'signed', {
            mode: 'bulk enroll + verbal',
            date: consentDate,
            dateSource: elid && dates[elid] ? 'first visit' : 'today',
          });
        }
      }));
      for (const r of results) {
        if (r.status === 'rejected' && r.reason && r.reason.status === 409) conflicts++;
      }
      const failed = results.filter(r => r.status === 'rejected').length;
      const ok = selPatients.length - failed;
      if (conflicts > 0) {
        flashToast(`Enrolled ${ok}; ${conflicts} updated elsewhere. Reloaded latest data; try again.`);
      } else if (failed > 0) {
        const firstErr = results.find(r => r.status === 'rejected');
        throw new Error((firstErr && firstErr.reason && firstErr.reason.message) || 'Enroll failed');
      } else {
        flashToast(`Enrolled ${selPatients.length} patient${selPatients.length !== 1 ? 's' : ''} in CCM`);
      }
      setSelected(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || 'Enroll failed');
    } finally {
      setBusy(false);
    }
  }

  // Bulk-enroll all selected patients into BHI 99484. Mirrors bulkEnroll (CCM)
  // but writes billing.bhiProgram + bhi.{dx,enrolledAt} and uses
  // program_slot: 'bhi' on the disposition. dx is taken from a current AI
  // suggestion if one is in state (dispositions[id]?.bhi?.suggestion_payload?.reasons[0]);
  // otherwise empty string — bhi.dx is metadata, the billing-note generator
  // derives the clinical dx narrative from chart + scale data at generate time.
  // Bulk-enroll selected patients into Chronic Pain Management (G3002).
  // Sets billing.painProgram only — the base CCM/APCM program and consent are
  // untouched when already present; a missing CCM consent is attested verbal
  // (same model as CCM enrollment). The confirm carries the two OPERATIONAL
  // attestations no chart heuristic can verify.
  // PCM enrolls exactly ONE condition per patient (disease-specific care plan
  // — CMS PCM element). The principal condition is captured AT enrollment and
  // persisted to billing.pcmCondition: auto-filled for single-condition
  // charts, provider-chosen in the modal for multi-condition charts.
  // Persisting even the auto case pins the designation — a second condition
  // syncing from Elation later can never silently change which disease the
  // notes and the disease-specific plan document (pcm-principal.js would
  // otherwise return null and the PCM bill would gate off).
  function bulkEnrollPcm(program) {
    const rows = selPatients.map((p) => {
      // value = the matching text pcm-principal resolves against; label adds
      // the ICD-10 code(s) so the provider picks against real diagnosis
      // evidence, not bare wording (codex review 2026-07-20).
      const options = (Array.isArray(p.raw.conditions) ? p.raw.conditions : [])
        .filter(Boolean)
        .map((c) => {
          const value = pcmConditionText(c);
          if (!value) return null;
          const icd = c && typeof c === 'object'
            ? (Array.isArray(c.icd10) ? c.icd10 : (c.icd10 ? [c.icd10] : [])).filter(Boolean).join(', ')
            : '';
          return { value, label: icd ? `${value} (${icd})` : value };
        })
        .filter(Boolean);
      return { p, options, choice: options.length === 1 ? options[0].value : '' };
    });
    const noConditions = rows.filter((r) => r.options.length === 0);
    if (noConditions.length) {
      setError(`PCM requires a documented condition — none on file for: ${noConditions.map((r) => r.p.name).join(', ')}`);
      return;
    }
    setPcmAssign({ program, rows });
  }

  // Modal confirm: same write/consent/conflict shape as bulkEnroll, plus the
  // per-patient billing.pcmCondition write. The modal's Enroll click carries
  // the verbal-consent attestation (single dialog — no second confirm).
  async function confirmPcmEnroll() {
    if (!pcmAssign) return;
    const { program, rows } = pcmAssign;
    setBusy(true); setError(null);
    try {
      const elationIds = rows
        .map(({ p }) => (p.raw.elation_id ? String(p.raw.elation_id) : null))
        .filter(Boolean);
      let dates = {};
      if (elationIds.length) {
        try { dates = (await ccmApi.firstVisitDates(elationIds)).results || {}; }
        catch (_) { dates = {}; }
      }
      const today = new Date().toISOString().slice(0, 10);
      let conflicts = 0;
      const results = await Promise.allSettled(rows.map(async ({ p, choice }) => {
        const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
        const consentDate = (elid && dates[elid]) || today;
        const prevConsent = p.raw.consent || p.consent || 'pending';
        const raw = { ...p.raw };
        applyEnrollment(raw, program, consentDate);
        raw.billing = { ...raw.billing, pcmCondition: choice };
        await ccmApi.updatePatient(p.id, raw);
        if (prevConsent !== 'signed' && raw.consent === 'signed') {
          await auditConsentChange({ ...p, _raw: p.raw }, prevConsent, 'signed', {
            mode: 'bulk enroll + verbal',
            date: consentDate,
            dateSource: elid && dates[elid] ? 'first visit' : 'today',
          });
        }
        // Dedicated PCM-enrollment audit (codex review 2026-07-20): the
        // consent audit above only fires on a pending→signed transition, so
        // an already-consented patient's PCM enrollment — program, chosen
        // principal condition, and the eligibility attestation — would
        // otherwise leave no dedicated trail.
        await safeAudit('PCM_ENROLL', p.id,
          `PCM ${program} enrolled for ${p.name} — principal condition: "${choice}"; `
          + 'operator attested a single complex chronic condition expected to last ≥3 months '
          + 'with significant risk of hospitalization, acute exacerbation/decompensation, functional decline, or death');
      }));
      for (const r of results) {
        if (r.status === 'rejected' && r.reason && r.reason.status === 409) conflicts++;
      }
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = rows.length - failed;
      if (conflicts > 0) {
        flashToast(`Enrolled ${ok}; ${conflicts} updated elsewhere. Reloaded latest data; try again.`);
      } else if (failed > 0) {
        const firstErr = results.find((r) => r.status === 'rejected');
        throw new Error((firstErr && firstErr.reason && firstErr.reason.message) || 'Enroll failed');
      } else {
        flashToast(`Enrolled ${rows.length} patient${rows.length !== 1 ? 's' : ''} in PCM ${program}`);
      }
      setPcmAssign(null);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || 'Enroll failed');
    } finally {
      setBusy(false);
    }
  }

  // PIN has no bulk enrollment: its qualifying condition must be captured per patient.
  // Bulk pain enrollment records the designated CPM billing provider + the
  // disclosures for every selected patient, but NOT an initiating visit — that
  // visit is per-patient and can't be shared, so it stays absent and each
  // patient's month gates remain blocked (backfill chip, rowCompletePain) until
  // it's recorded. CPM no longer rides the global CCM consent (P0.2), so this
  // path no longer touches raw.consent.
  async function bulkEnrollPain() {
    if (selPatients.length === 0) return;
    const n = selPatients.length;
    let providers;
    try { providers = await loadCpmProviderOptions(currentUser); }
    catch (_) { setError('Could not load the staff list — try again.'); return; }
    if (!providers.options.length) {
      setError('No CPM-authorized provider (MD/DO/NP/PA/CNM) on staff. Add one in Settings before enrolling in Chronic Pain Management.');
      return;
    }
    const res = await confirmDialog({
      title: `Enroll ${n} patient${n !== 1 ? 's' : ''} in Chronic Pain Management (G3002)?`,
      body: 'Bulk enrollment records the designated CPM billing provider and the disclosures below for each patient. The initiating in-person visit is per-patient and cannot be shared — each patient shows a "CPM record incomplete" chip and cannot bill G3002 until you record their visit. By confirming you attest, for each patient:',
      bullets: [...PAIN_ATTEST_BULLETS, PAIN_DURATION_BULLET, ...PAIN_DISCLOSURE_BULLETS],
      fields: [
        { key: 'practitionerId', label: 'Designated CPM billing practitioner', type: 'select', options: providers.options, required: true,
          value: providers.selfId },
      ],
      confirmLabel: 'Enroll',
    });
    if (!res.ok) return;
    const provider = providers.byId[res.values.practitionerId];
    if (!provider) { setError('Selected practitioner not found — try again.'); return; }
    setBusy(true); setError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const results = await Promise.allSettled(selPatients.map(async (p) => {
        const raw = { ...p.raw };
        raw.billing = { ...(raw.billing || {}), painProgram: 'G3002' };
        // P1.3 confirm-before-note-carries-it: bulk has no per-patient editor,
        // so it must not create medical-necessity text.
        const painRecord = { ...(raw.pain || {}), ...buildPainEnrollmentRecord({
          provider: { id: provider.id, name: provider.name },
          consentDate: today,
          durationOver3Months: true,
        }) };
        // Bulk collects no initiating visit. A pre-existing one is kept ONLY if
        // it belongs to the newly-designated provider; otherwise it would pair a
        // stale visit with a different billing provider, so drop it and let the
        // month stay blocked until the visit is re-recorded (backfill chip).
        if (painRecord.initiatingVisit && String(painRecord.initiatingVisit.practitionerId || '') !== String(provider.id)) {
          delete painRecord.initiatingVisit;
        }
        raw.pain = painRecord;
        await ccmApi.updatePatient(p.id, raw);
        await auditPainConsentChange({ ...p, _raw: p.raw }, painRecord, { note: 'bulk enroll — initiating visit pending' });
      }));
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed) {
        setError(`${failed} of ${n} pain enrollment${n !== 1 ? 's' : ''} failed — refresh and retry.`);
      } else {
        flashToast(`Enrolled ${n} patient${n !== 1 ? 's' : ''} in Pain Mgmt — record each initiating visit to enable billing`);
      }
      setSelected(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || 'Pain enrollment failed');
    } finally {
      setBusy(false);
    }
  }

  // Single-row pain enrollment — records the FULL CPM record (dedicated consent
  // w/ disclosures, the initiating in-person visit date + practitioner, and the
  // designated billing provider) so month gates can pass. Shared by the enroll
  // action and the 8b backfill chip (rowCompletePain). CPM no longer rides the
  // global CCM consent (P0.2), so this path no longer touches raw.consent.
  async function rowEnrollPain(p) {
    let providers;
    try { providers = await loadCpmProviderOptions(currentUser); }
    catch (_) { setError('Could not load the staff list — try again.'); return; }
    if (!providers.options.length) {
      setError('No CPM-authorized provider (MD/DO/NP/PA/CNM) on staff. Add one in Settings before enrolling in Chronic Pain Management.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const existingProvider = p.raw?.pain?.billingProvider?.id != null ? String(p.raw.pain.billingProvider.id) : '';
    // Prefill FACTS to minimize provider clicks: the visit date offers the
    // chart's most recent Elation office-visit date (editable), and the biller
    // defaults to the recorded provider, else the enrolling provider's own
    // record. The ≥30-min in-person attestation itself stays an explicit
    // confirm bullet — auto-selecting a date must never auto-attest what
    // happened at that visit.
    let visitPrefill = p.raw?.pain?.initiatingVisit?.date || '';
    if (!visitPrefill && p.raw?.elation_id) {
      try {
        const dates = (await ccmApi.firstVisitDates([String(p.raw.elation_id)])).results || {};
        const d = dates[String(p.raw.elation_id)];
        if (d && d <= today) visitPrefill = d;
      } catch (_) { /* prefill only — blank field on failure */ }
    }
    const res = await confirmDialog({
      title: `Chronic Pain Management (G3002) — ${p.name}`,
      body: 'Record the CPM consent and the initiating in-person visit. By confirming you attest:',
      bullets: [...PAIN_ATTEST_BULLETS_ROW, PAIN_DURATION_BULLET, ...PAIN_DISCLOSURE_BULLETS],
      fields: [
        { key: 'visitDate', label: 'Initiating in-person visit date', type: 'date', required: true, max: today,
          value: visitPrefill },
        { key: 'practitionerId', label: 'Designated CPM billing practitioner', type: 'select', options: providers.options, required: true,
          value: providers.byId[existingProvider] ? existingProvider : providers.selfId },
        { key: 'necessity', label: 'Medical necessity (drafted from chart — edit as needed)', type: 'textarea', required: false,
          value: p.raw?.pain?.necessity?.text || buildPainNecessityDraft(p.raw) },
        // No chart prefill: unlike necessity there is no derivable draft for
        // deliberate exclusions — blank unless the provider states them.
        { key: 'excluded', label: 'Treatments deliberately excluded + rationale (optional)', type: 'textarea', required: false,
          value: p.raw?.pain?.excludedTreatments?.text || '' },
      ],
      confirmLabel: 'Save CPM record',
    });
    if (!res.ok) return;
    const provider = providers.byId[res.values.practitionerId];
    if (!provider) { setError('Selected practitioner not found — try again.'); return; }
    // Fail-closed backstop: the dialog already blocks a future date, but never
    // let a future initiating-visit date reach a billing write (it would
    // unblock G3002 on a nonexistent visit).
    if (!res.values.visitDate || res.values.visitDate > today) {
      setError('Initiating-visit date must be on or before today.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const raw = { ...p.raw };
      raw.billing = { ...(raw.billing || {}), painProgram: 'G3002' };
      // Necessity re-stamps ONLY when the operator actually changed the text —
      // an untouched field on a later save (e.g. fixing the visit date) must
      // not replace the original confirmer/date. confirmedBy is the acting
      // user, not the designated provider they selected.
      const enteredNecessity = String(res.values.necessity || '').replace(/\s+/g, ' ').trim();
      const storedNecessity = String(p.raw?.pain?.necessity?.text || '');
      const enteredExcluded = String(res.values.excluded || '').replace(/\s+/g, ' ').trim();
      const storedExcluded = String(p.raw?.pain?.excludedTreatments?.text || '');
      const painRecord = { ...(raw.pain || {}), ...buildPainEnrollmentRecord({
        provider: { id: provider.id, name: provider.name },
        initiatingVisitDate: res.values.visitDate,
        consentDate: today,
        durationOver3Months: true,
        necessity: enteredNecessity !== storedNecessity ? enteredNecessity : undefined,
        excludedTreatments: enteredExcluded !== storedExcluded ? enteredExcluded : undefined,
        confirmedBy: currentUser
          ? { id: currentUser.id, name: String(currentUser.displayName || currentUser.name || currentUser.email || '').trim() }
          : null,
      }) };
      // Blanking the exclusions field on a reopen RESCINDS the record —
      // unlike necessity (static enrollment fact), a withdrawn exclusion must
      // stop reaching future care-plan prompts (adversarial review finding 7).
      if (enteredExcluded === '' && storedExcluded) delete painRecord.excludedTreatments;
      raw.pain = painRecord;
      await ccmApi.updatePatient(p.id, raw);
      await auditPainConsentChange({ ...p, _raw: p.raw }, painRecord, { note: 'onboard row enroll' });
      flashToast(`Saved CPM record for ${p.name}`);
      await refresh();
    } catch (e) {
      if (e && e.status === 409) {
        // Another user updated this patient before our save (stale _updatedAt).
        // Refresh to the latest and prompt a retry — same soft flow as rowAccept
        // / aiApply / saveEdit (codex finding 8).
        flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`);
        await refresh();
      } else {
        setError(e?.message || 'Pain enrollment failed');
      }
    } finally {
      setBusy(false);
    }
  }

  // Single-row PIN enrollment records the dedicated consent, per-patient
  // qualifying condition, initiating visit, and navigator-training attestation.
  async function rowEnrollPin(p) {
    let providers;
    try { providers = await loadCpmProviderOptions(currentUser); }
    catch (_) { setError('Could not load the staff list for PIN/G0023 — try again.'); return; }
    if (!providers.options.length) {
      setError('No PIN initiating-visit practitioner (MD/DO/NP/PA/CNM) on staff. Add one in Settings before enrolling in PIN/G0023.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    const existingPractitioner = p.raw?.pin?.initiatingVisit?.practitionerId != null
      ? String(p.raw.pin.initiatingVisit.practitionerId) : '';
    let visitPrefill = p.raw?.pin?.initiatingVisit?.date || '';
    if (!visitPrefill && p.raw?.elation_id) {
      try {
        const dates = (await ccmApi.firstVisitDates([String(p.raw.elation_id)])).results || {};
        const d = dates[String(p.raw.elation_id)];
        if (d && d <= today) visitPrefill = d;
      } catch (_) { /* prefill only — blank field on failure */ }
    }
    const res = await confirmDialog({
      title: `Principal Illness Navigation (G0023) — ${p.name}`,
      body: 'Record the PIN consent, qualifying condition, and initiating visit. By confirming you attest:',
      bullets: [...PIN_ATTEST_BULLETS_ROW, ...PIN_DISCLOSURE_BULLETS],
      fields: [
        { key: 'qualifyingCondition', label: 'Qualifying serious high-risk condition', type: 'text', required: true,
          value: p.raw?.pin?.qualifyingCondition || '' },
        { key: 'visitDate', label: 'Initiating visit date', type: 'date', required: true, max: today,
          value: visitPrefill },
        { key: 'practitionerId', label: 'Initiating-visit billing practitioner', type: 'select', options: providers.options, required: true,
          value: providers.byId[existingPractitioner] ? existingPractitioner : providers.selfId },
      ],
      confirmLabel: 'Save PIN record',
    });
    if (!res.ok) return;
    const provider = providers.byId[res.values.practitionerId];
    if (!provider) { setError('Selected PIN/G0023 practitioner not found — try again.'); return; }
    // Fail closed even if the dialog's date constraint is bypassed.
    if (!res.values.visitDate || res.values.visitDate > today) {
      setError('Initiating-visit date must be on or before today.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const raw = { ...p.raw };
      raw.billing = { ...(raw.billing || {}), navProgram: 'G0023' };
      const pinRecord = { ...(raw.pin || {}), ...buildPinEnrollmentRecord({
        provider: { id: provider.id, name: provider.name },
        initiatingVisitDate: res.values.visitDate,
        consentDate: today,
        qualifyingCondition: res.values.qualifyingCondition,
      }) };
      raw.pin = pinRecord;
      await ccmApi.updatePatient(p.id, raw);
      await auditPinConsentChange({ ...p, _raw: p.raw }, pinRecord, { note: 'onboard row enroll' });
      flashToast(`Saved PIN record for ${p.name}`);
      await refresh();
    } catch (e) {
      if (e && e.status === 409) {
        // Preserve the newer patient blob and require an explicit retry.
        flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`);
        await refresh();
      } else {
        setError(e?.message || 'PIN/G0023 enrollment failed');
      }
    } finally {
      setBusy(false);
    }
  }

  async function bulkEnrollBhi() {
    if (selPatients.length === 0) return;
    const code = '99484';
    const n = selPatients.length;
    if (!(await confirmDialog({
      title: `Enroll ${n} patient${n !== 1 ? 's' : ''} in BHI (${code})?`,
      bullets: [ATTEST_VERBAL_EACH],
      confirmLabel: 'Enroll',
    }))) return;
    setBusy(true); setError(null);
    try {
      const elationIds = selPatients
        .map(p => p.raw.elation_id ? String(p.raw.elation_id) : null)
        .filter(Boolean);
      let dates = {};
      if (elationIds.length) {
        try { dates = (await ccmApi.firstVisitDates(elationIds)).results || {}; }
        catch (_) { dates = {}; }
      }
      const today = new Date().toISOString().slice(0, 10);
      let conflicts = 0;
      const results = await Promise.allSettled(selPatients.map(async (p) => {
        const aiPayload = dispositions[p.id]?.bhi?.suggestion_payload || null;
        const reasons = Array.isArray(aiPayload?.reasons) ? aiPayload.reasons : [];
        const dx = (reasons[0] || (p.raw && p.raw.bhi && p.raw.bhi.dx) || '').toString();
        const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
        const consentDate = (elid && dates[elid]) || today;
        const raw = { ...p.raw };
        const prevBhiConsent = p.raw.bhiConsent || null;
        applyBhiEnrollment(raw, code, consentDate);
        if (dx) raw.bhi.dx = dx;
        await ccmApi.updatePatient(p.id, raw);
        if ((!prevBhiConsent || prevBhiConsent.signed !== true) && raw.bhiConsent.signed === true) {
          await auditBhiConsentChange({ ...p, _raw: p.raw }, prevBhiConsent, raw.bhiConsent, {
            mode: 'bulk enroll BHI + verbal',
            date: consentDate,
            dateSource: elid && dates[elid] ? 'first visit' : 'today',
            source: 'verbal',
          });
        }
        await ccmApi.upsertDisposition({
          patient_id: p.id,
          suggestion_hash: suggestionHash('BHI', code),
          program_slot: 'bhi',
          program_family: 'BHI',
          recommended_code: code,
          status: 'implemented',
          review_month: month,
          suggestion_payload: aiPayload || { recommendedCode: code, programFamily: 'BHI', source: 'bulk_manual' },
        });
      }));
      for (const r of results) {
        if (r.status === 'rejected' && r.reason && r.reason.status === 409) conflicts++;
      }
      const failed = results.filter(r => r.status === 'rejected').length;
      const ok = selPatients.length - failed;
      if (conflicts > 0) {
        flashToast(`Enrolled ${ok} in BHI; ${conflicts} updated elsewhere. Reloaded latest data; try again.`);
      } else if (failed > 0) {
        const firstErr = results.find(r => r.status === 'rejected');
        throw new Error((firstErr && firstErr.reason && firstErr.reason.message) || 'BHI enroll failed');
      } else {
        flashToast(`Enrolled ${selPatients.length} patient${selPatients.length !== 1 ? 's' : ''} in BHI ${code}`);
      }
      setSelected(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || 'BHI enroll failed');
    } finally {
      setBusy(false);
    }
  }

  function actionableBhiSuggestion(p) {
    const disp = dispositions[p.id]?.bhi;
    if (disp) {
      if (dispositionStillNeedsEnrollment(p, 'bhi', disp)) {
        const sugg = disp.suggestion_payload;
        if (sugg && sugg.recommendedCode && sugg.recommendedCode !== 'none') return sugg;
        return p.localBhiSuggestion || null;
      }
      if (disp.status && disp.status !== 'pending') return null;
      const sugg = disp.suggestion_payload;
      if (sugg && sugg.recommendedCode && sugg.recommendedCode !== 'none') return sugg;
      return null;
    }
    return p.localBhiSuggestion || null;
  }

  function ruleSuggestionTargetsFor(p) {
    const ccmSugg = (
      (programFilter === 'ccm' || programFilter === 'all') &&
      isUnenrolledCcm(p) &&
      p.suggestion &&
      p.suggestion.program &&
      p.suggestion.program !== 'none'
    ) ? p.suggestion : null;
    // A saved BHI disposition owns the row's BHI suggestion line unless a
    // previous "accepted" disposition did not actually enroll the patient.
    const bhiSugg = (
      (programFilter === 'bhi' || programFilter === 'all') &&
      isUnenrolledBhi(p) &&
      (!dispositions[p.id]?.bhi || dispositionStillNeedsEnrollment(p, 'bhi', dispositions[p.id]?.bhi)) &&
      p.localBhiSuggestion
    ) ? p.localBhiSuggestion : null;
    return { ccmSugg, bhiSugg };
  }

  function pendingLlmTargetsFor(p) {
    const slots = dispositions[p.id] || {};
    const ccmSugg = (
      (programFilter === 'ccm' || programFilter === 'all') &&
      isUnenrolledCcm(p) &&
      slots.ccm?.status === 'pending'
    ) ? normalizeCcmSuggestionForDisposition(slots.ccm.suggestion_payload) : null;
    const rawBhi = slots.bhi?.suggestion_payload;
    const bhiSugg = (
      (programFilter === 'bhi' || programFilter === 'all') &&
      isUnenrolledBhi(p) &&
      slots.bhi?.status === 'pending' &&
      rawBhi &&
      rawBhi.recommendedCode &&
      rawBhi.recommendedCode !== 'none'
    ) ? rawBhi : null;
    return { ccmSugg, bhiSugg };
  }

  async function applySuggestionRows(rows, sourceLabel) {
    const elationIds = rows.filter(r => r.ccmSugg || r.bhiSugg)
      .map(r => r.p.raw.elation_id ? String(r.p.raw.elation_id) : null)
      .filter(Boolean);
    let dates = {};
    if (elationIds.length) {
      try { dates = (await ccmApi.firstVisitDates(elationIds)).results || {}; }
      catch (_) { dates = {}; }
    }
    const today = new Date().toISOString().slice(0, 10);
    let ccmOk = 0, bhiOk = 0, conflicts = 0;
    for (const { p, ccmSugg, bhiSugg } of rows) {
      try {
        const raw = { ...p.raw };
        const prevConsent = p.raw.consent || p.consent || 'pending';
        const prevBhiConsent = p.raw.bhiConsent || null;
        let ccmConsentDate = today;
        if (ccmSugg) {
          const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
          ccmConsentDate = (elid && dates[elid]) || today;
          const code = ccmSugg.recommendedCode || ccmSugg.program;
          applyEnrollment(raw, code, ccmConsentDate);
        }
        let bhiConsentDate = today;
        if (bhiSugg) {
          const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
          bhiConsentDate = (elid && dates[elid]) || today;
          const code = String(bhiSugg.recommendedCode || '99484').trim() || '99484';
          const reasons = Array.isArray(bhiSugg?.reasons) ? bhiSugg.reasons : [];
          const dx = (reasons[0] || (raw.bhi && raw.bhi.dx) || '').toString();
          applyBhiEnrollment(raw, code, bhiConsentDate);
          if (dx) raw.bhi.dx = dx;
        }

        await ccmApi.updatePatient(p.id, raw);

        if (ccmSugg && prevConsent !== 'signed' && raw.consent === 'signed') {
          const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
          await auditConsentChange({ ...p, _raw: p.raw }, prevConsent, 'signed', {
            mode: `bulk ${sourceLabel} CCM verbal`,
            date: ccmConsentDate,
            dateSource: elid && dates[elid] ? 'first visit' : 'today',
          });
        }
        if (bhiSugg && (!prevBhiConsent || prevBhiConsent.signed !== true) && raw.bhiConsent.signed === true) {
          const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
          await auditBhiConsentChange({ ...p, _raw: p.raw }, prevBhiConsent, raw.bhiConsent, {
            mode: `bulk ${sourceLabel} BHI verbal`,
            date: bhiConsentDate,
            dateSource: elid && dates[elid] ? 'first visit' : 'today',
            source: 'verbal',
          });
        }
        if (ccmSugg?.programFamily || ccmSugg?.recommendedCode) {
          const ccmNorm = normalizeCcmSuggestionForDisposition(ccmSugg);
          if (ccmNorm) {
            await ccmApi.upsertDisposition({
              patient_id: p.id,
              suggestion_hash: suggestionHash(ccmNorm.programFamily, ccmNorm.recommendedCode),
              program_slot: 'ccm',
              program_family: ccmNorm.programFamily,
              recommended_code: ccmNorm.recommendedCode,
              status: 'implemented',
              review_month: month,
              suggestion_payload: ccmNorm,
            });
          }
        }
        if (bhiSugg) {
          const code = String(bhiSugg.recommendedCode || '99484').trim() || '99484';
          await ccmApi.upsertDisposition({
            patient_id: p.id,
            suggestion_hash: suggestionHash(bhiSugg.programFamily || 'BHI', code),
            program_slot: 'bhi',
            program_family: bhiSugg.programFamily || 'BHI',
            recommended_code: code,
            status: 'implemented',
            review_month: month,
            suggestion_payload: bhiSugg,
          });
          bhiOk++;
        }
        if (ccmSugg) ccmOk++;
      } catch (e) {
        if (e && e.status === 409) conflicts++;
        else throw e;
      }
    }
    return { ccmOk, bhiOk, conflicts };
  }

  // Bulk: accept visible deterministic suggestions. LLM-reviewed suggestions
  // have their own button/count so operators know exactly what will be applied.
  async function bulkAcceptRuleSuggestions() {
    if (selPatients.length === 0) return;
    const rows = selPatients.map((p) => {
      const { ccmSugg, bhiSugg } = ruleSuggestionTargetsFor(p);
      return { p, ccmSugg, bhiSugg };
    }).filter(r => r.ccmSugg || r.bhiSugg);
    const count = rows.reduce((n, r) => n + (r.ccmSugg ? 1 : 0) + (r.bhiSugg ? 1 : 0), 0);
    const skipped = selPatients.length - rows.length;
    if (count === 0) {
      flashToast(`No rule-based suggestions in selection (${skipped} skipped)`);
      return;
    }
    if (!(await confirmDialog({
      title: `Apply ${count} rule-based suggestion${count !== 1 ? 's' : ''} across ${rows.length} patient${rows.length !== 1 ? 's' : ''}?`,
      bullets: [ATTEST_VERBAL_EACH],
      confirmLabel: 'Apply',
    }))) return;
    setBusy(true); setError(null);
    try {
      const { ccmOk, bhiOk, conflicts } = await applySuggestionRows(rows, 'rule-based');
      const parts = [];
      if (ccmOk) parts.push(`${ccmOk} CCM`);
      if (bhiOk) parts.push(`${bhiOk} BHI`);
      if (skipped) parts.push(`${skipped} skipped`);
      if (conflicts) parts.push(`${conflicts} conflict${conflicts !== 1 ? 's' : ''}`);
      flashToast(`Applied rule suggestions: ${parts.join(', ')}`);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || 'Accept rule suggestions failed');
    } finally {
      setBusy(false);
    }
  }

  async function bulkAcceptLlmSuggestions() {
    if (selPatients.length === 0) return;
    const rows = selPatients.map((p) => {
      const { ccmSugg, bhiSugg } = pendingLlmTargetsFor(p);
      return { p, ccmSugg, bhiSugg };
    }).filter(r => r.ccmSugg || r.bhiSugg);
    const count = rows.reduce((n, r) => n + (r.ccmSugg ? 1 : 0) + (r.bhiSugg ? 1 : 0), 0);
    const skipped = selPatients.length - rows.length;
    if (count === 0) {
      flashToast(`No pending LLM-reviewed recommendations in selection (${skipped} skipped)`);
      return;
    }
    if (!(await confirmDialog({
      title: `Apply ${count} LLM-reviewed recommendation${count !== 1 ? 's' : ''} across ${rows.length} patient${rows.length !== 1 ? 's' : ''}?`,
      bullets: ['Attests verbal consent obtained for each enrollment. Audit-logged.'],
      confirmLabel: 'Apply',
    }))) return;
    setBusy(true); setError(null);
    try {
      const { ccmOk, bhiOk, conflicts } = await applySuggestionRows(rows, 'LLM-reviewed');
      const parts = [];
      if (ccmOk) parts.push(`${ccmOk} CCM`);
      if (bhiOk) parts.push(`${bhiOk} BHI`);
      if (skipped) parts.push(`${skipped} skipped`);
      if (conflicts) parts.push(`${conflicts} conflict${conflicts !== 1 ? 's' : ''}`);
      flashToast(`Applied LLM-reviewed recommendations: ${parts.join(', ')}`);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || 'Accept LLM-reviewed suggestions failed');
    } finally {
      setBusy(false);
    }
  }

  // AI Panel Review: ship selected rows to Claude via /ai, parse, upsert
  // dispositions, and merge results into `dispositions` state. Mirrors v2's
  // runPanelReview but scoped to the user's selection (no seedPanelCandidates
  // step — selection IS the candidate set).
  async function bulkAIReview() {
    if (selPatients.length === 0) return;
    setBusy(true);
    setError(null);

    // Mark in-flight for UI pulse. Reviewing-state is a CCM-slot affordance
    // (SuggestionLine drives off _aiReview); BHI pulse mirrors but doesn't need
    // a parallel "reviewing" state because the per-patient call returns both
    // suggestions together — the CCM pulse covers the AI-Review affordance.
    setDispositions((prev) => {
      const next = { ...prev };
      for (const p of selPatients) {
        const prior = prev[p.id] || { ccm: null, bhi: null };
        next[p.id] = {
          ...prior,
          ccm: { ...(prior.ccm || {}), status: 'reviewing', _local: true },
        };
      }
      return next;
    });

    try {
      // 1. Build alias map + rows.
      const aliasMap = {};
      const rows = selPatients.map((p, i) => {
        const { alias, row, mapEntry } = buildSafeHarborPanelRow(p.raw, i + 1);
        aliasMap[alias] = mapEntry;
        return row;
      });

      // 2. Chunk (50 per request — v2's number).
      const chunks = chunkRows(rows, 50);
      const aliasesByChunk = chunks.map((c) => c.map((r) => r.alias));

      // 3. Send each chunk; collect parsed results.
      const collected = [];
      const failedAliases = new Set();
      for (let ci = 0; ci < chunks.length; ci++) {
        const userPrompt = PANEL_REVIEW_USER_TEMPLATE + JSON.stringify(chunks[ci], null, 2);
        let parsed;
        try {
          const text = await ccmApi.aiCall({
            system: PANEL_REVIEW_SYSTEM_PROMPT,
            userPrompt,
            maxTokens: 8192,
          });
          parsed = parsePanelReviewResponse(text, aliasesByChunk[ci]);
        } catch (e) {
          // Retry once with a stricter instruction (matches v2 behavior).
          try {
            const text2 = await ccmApi.aiCall({
              system: PANEL_REVIEW_SYSTEM_PROMPT,
              userPrompt: userPrompt + '\n\nIMPORTANT: Your previous response was not valid JSON. Return JSON only, no narrative.',
              maxTokens: 8192,
            });
            parsed = parsePanelReviewResponse(text2, aliasesByChunk[ci]);
          } catch (e2) {
            // Chunk failed irretrievably — mark its aliases as failed.
            for (const a of aliasesByChunk[ci]) failedAliases.add(a);
            continue;
          }
        }
        for (const pat of parsed.patients) collected.push(pat);
      }

      // 4. Re-identify and persist. Build the next-state map up front so the
      // setDispositions call below merges over the latest prev (avoids stale
      // closure read of the dispositions state captured at handler entry).
      // merged is keyed patient_id → { ccm: dispRow|null, bhi: dispRow|null }.
      const merged = {};
      const failedPatientIds = new Set();
      let noPickCount = 0;
      let persistFailedCount = 0;
      let persistedCount = 0;
      for (const a of failedAliases) {
        const failedEntry = aliasMap[a];
        if (failedEntry) failedPatientIds.add(failedEntry.patientId);
      }
      for (const result of collected) {
        const entry = aliasMap[result.alias];
        if (!entry) continue;
        // Dual-pick: a single panel-review call can yield one suggestion per
        // program family. Persist each into its own slot so the row UI can
        // surface both Accept blocks independently. APCM/Psych/etc. still
        // cannot drive Onboard enrollment — only CCM + BHI are wired here.
        const ccmPick = pickCcmSuggestion(result.suggestions || []);
        const bhiPick = pickBhiSuggestion(result.suggestions || []);

        if (!ccmPick && !bhiPick) {
          noPickCount++;
          continue;
        }

        if (!merged[entry.patientId]) merged[entry.patientId] = { ccm: null, bhi: null };

        if (ccmPick) {
          const hash = suggestionHash(ccmPick.programFamily, ccmPick.recommendedCode);
          const payload = {
            patient_id: entry.patientId,
            suggestion_hash: hash,
            program_slot: 'ccm',
            program_family: ccmPick.programFamily,
            recommended_code: ccmPick.recommendedCode,
            status: 'pending',
            review_month: month,
            suggestion_payload: { ...ccmPick, allSuggestions: result.suggestions },
          };
          try {
            await ccmApi.upsertDisposition(payload);
            persistedCount++;
            merged[entry.patientId].ccm = {
              patient_id: entry.patientId,
              suggestion_hash: hash,
              status: 'pending',
              suggestion_payload: payload.suggestion_payload,
              updated_at: new Date().toISOString(),
            };
          } catch (e) {
            persistFailedCount++;
            failedPatientIds.add(entry.patientId);
            console.warn('CCM disposition upsert failed for', entry.patientId, e);
          }
        }

        if (bhiPick) {
          const hash = suggestionHash(bhiPick.programFamily, bhiPick.recommendedCode);
          const payload = {
            patient_id: entry.patientId,
            suggestion_hash: hash,
            program_slot: 'bhi',
            program_family: bhiPick.programFamily,
            recommended_code: bhiPick.recommendedCode,
            status: 'pending',
            review_month: month,
            suggestion_payload: { ...bhiPick, allSuggestions: result.suggestions },
          };
          try {
            await ccmApi.upsertDisposition(payload);
            persistedCount++;
            merged[entry.patientId].bhi = {
              patient_id: entry.patientId,
              suggestion_hash: hash,
              status: 'pending',
              suggestion_payload: payload.suggestion_payload,
              updated_at: new Date().toISOString(),
            };
          } catch (e) {
            persistFailedCount++;
            failedPatientIds.add(entry.patientId);
            console.warn('BHI disposition upsert failed for', entry.patientId, e);
          }
        }
      }
      // Functional setter so we merge over the latest in-flight state rather
      // than the snapshot captured when bulkAIReview was invoked. Selected
      // patients without a returned result revert to no disposition (delete).
      setDispositions((prev) => {
        const next = { ...prev };
        for (const p of selPatients) delete next[p.id];
        for (const [pid, disp] of Object.entries(merged)) next[pid] = disp;
        return next;
      });

      const reviewedCount = collected.length;
      const failedCount = failedAliases.size;
      const parts = [`Reviewed ${reviewedCount}`];
      if (persistedCount > 0) parts.push(`Saved ${persistedCount} suggestion${persistedCount !== 1 ? 's' : ''}`);
      if (noPickCount > 0) parts.push(`${noPickCount} no enrollable match`);
      if (failedCount > 0) parts.push(`${failedCount} failed`);
      if (persistFailedCount > 0) parts.push(`${persistFailedCount} not saved`);
      flashToast(parts.join(' · '));
      if (persistFailedCount > 0) {
        setError(`${persistFailedCount} AI review suggestion${persistFailedCount !== 1 ? 's' : ''} failed to save. Retry selected rows.`);
      }
      if (failedPatientIds.size > 0) {
        setSelected((prev) => {
          const next = new Set(prev);
          for (const p of selPatients) {
            if (!failedPatientIds.has(p.id)) next.delete(p.id);
          }
          return next;
        });
      } else {
        setSelected(new Set());
      }
    } catch (e) {
      setError(e?.message || 'AI review failed');
    } finally {
      setBusy(false);
    }
  }

  async function bulkDecline(reason, mode = 'ccm') {
    // mode ∈ {'ccm', 'bhi', 'both'} — drives which program(s) get declined per row.
    if (selPatients.length === 0) return;
    setBusy(true); setError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const nowIso = new Date().toISOString();
      let conflicts = 0, ccmOk = 0, bhiOk = 0;
      const results = await Promise.allSettled(selPatients.map(async (p) => {
        let raw = { ...p.raw };
        let didCcm = false, didBhi = false;
        if (mode === 'bhi' || mode === 'both') {
          const priorBhi = (raw.bhi) || {};
          raw = { ...raw, bhi: { ...priorBhi, declined: true, declinedReason: reason, declinedAt: nowIso } };
          didBhi = true;
        }
        if (mode === 'ccm' || mode === 'both') {
          const prevConsent = p.raw.consent || p.consent || 'pending';
          raw = { ...raw, consent: 'declined', consentDate: today, consentDeclinedReason: reason };
          clearConsentLinkProvenance(raw);
          await ccmApi.updatePatient(p.id, raw);
          await auditConsentChange({ ...p, _raw: p.raw }, prevConsent, 'declined', {
            mode: 'declined',
            date: today,
            reason,
          });
          didCcm = true;
        } else {
          // bhi-only path still needs the PUT.
          await ccmApi.updatePatient(p.id, raw);
        }
        return { didCcm, didBhi };
      }));
      for (const r of results) {
        if (r.status === 'rejected' && r.reason && r.reason.status === 409) conflicts++;
        if (r.status === 'fulfilled') {
          if (r.value.didCcm) ccmOk++;
          if (r.value.didBhi) bhiOk++;
        }
      }
      const failed = results.filter(r => r.status === 'rejected').length;
      const parts = [];
      if (ccmOk) parts.push(`${ccmOk} CCM`);
      if (bhiOk) parts.push(`${bhiOk} BHI`);
      if (conflicts) parts.push(`${conflicts} conflict${conflicts !== 1 ? 's' : ''}`);
      if (conflicts > 0 || failed === 0) {
        flashToast(`Marked declined: ${parts.join(', ') || 'nothing'}`);
      } else {
        const firstErr = results.find(r => r.status === 'rejected');
        throw new Error((firstErr && firstErr.reason && firstErr.reason.message) || 'Decline failed');
      }
      setSelected(new Set());
      setDeclineOpen(false);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Decline failed');
      setDeclineOpen(false);
    } finally {
      setBusy(false);
    }
  }

  async function unenrollPatients(targetPatients, reason, programSlot = 'ccm') {
    if (!targetPatients.length) return;
    setBusy(true); setError(null);
    try {
      let conflicts = 0;
      const results = await Promise.allSettled(targetPatients.map(async (p) => {
        await ccmApi.updatePatient(p.id, buildUnenrolledRaw(p, month, reason, programSlot));
      }));
      for (const r of results) {
        if (r.status === 'rejected' && r.reason && r.reason.status === 409) conflicts++;
      }
      const failed = results.filter(r => r.status === 'rejected').length;
      const ok = targetPatients.length - failed;
      const label = programSlot === 'bhi' ? 'Unenrolled from BHI' : 'Unenrolled';
      if (conflicts > 0) {
        flashToast(`${label} ${ok}; ${conflicts} updated elsewhere. Reloaded latest data; try again.`);
      } else if (failed > 0) {
        const firstErr = results.find(r => r.status === 'rejected');
        throw new Error((firstErr && firstErr.reason && firstErr.reason.message) || 'Unenroll failed');
      } else {
        flashToast(`${label} ${targetPatients.length} patient${targetPatients.length !== 1 ? 's' : ''}`);
      }
      setSelectedEnrolled(new Set());
      setUnenrollOpen(null);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Unenroll failed');
      setUnenrollOpen(null);
    } finally {
      setBusy(false);
    }
  }

  // Per-row: adopt the AI suggestion for one patient. "Don't enroll"
  // suggestions don't get a row Accept — those rows show "Mark declined" instead.
  async function rowAccept(p) {
    if (p.suggestion.program === 'none') return;
    const label = formatCcmSuggestionLabel(p.suggestion);
    const tierNote = CCM_TIER_ENROLL_NORMALIZE[p.suggestion.program]
      ? 'Enrolls at CCM base (99490) — the billed tier is decided monthly by documented time + MDM.'
      : '';
    if (!(await confirmDialog({
      title: `Enroll ${p.name} in ${label}?`,
      body: tierNote || undefined,
      bullets: [ATTEST_VERBAL],
      confirmLabel: 'Enroll',
    }))) return;
    setBusy(true); setError(null);
    try {
      let consentDate = new Date().toISOString().slice(0, 10);
      if (p.raw.elation_id && p.consent !== 'signed') {
        try {
          const res = await ccmApi.firstVisitDates([String(p.raw.elation_id)]);
          consentDate = (res.results && res.results[String(p.raw.elation_id)]) || consentDate;
        } catch (_) {}
      }
      const raw = { ...p.raw };
      const prevConsent = p.raw.consent || p.consent || 'pending';
      applyEnrollment(raw, p.suggestion.program, consentDate);
      try {
        await ccmApi.updatePatient(p.id, raw);
        if (prevConsent !== 'signed' && raw.consent === 'signed') {
          await auditConsentChange({ ...p, _raw: p.raw }, prevConsent, 'signed', {
            mode: 'AI suggestion verbal',
            date: consentDate,
          });
        }
        flashToast(`Enrolled ${p.name} in ${label}`);
      } catch (err) {
        if (err && err.status === 409) {
          flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`);
        } else { throw err; }
      }
      await refresh();
    } catch (e) {
      setError(e?.message || 'Enroll failed');
    } finally {
      setBusy(false);
    }
  }

  // saveEdit — EditPatientModal hands us `updatedRaw` built from p.raw, so
  // _updatedAt is naturally preserved on the body (raw is GET /data/patients'
  // top-level row, which carries _updatedAt). On 409, surface a soft toast and
  // refresh so the operator can re-edit against fresh data.
  async function saveEdit(updatedRaw) {
    try {
      await ccmApi.updatePatient(editPatient.id, updatedRaw);
      flashToast(`Updated ${updatedRaw.fname || ''} ${updatedRaw.lname || ''}`.trim());
    } catch (err) {
      if (err && err.status === 409) {
        flashToast('Patient was updated elsewhere. Reloaded latest data; try again.');
      } else { throw err; }
    }
    await refresh();
  }

  // Apply an AI-upgraded suggestion: enroll patient in the recommended program
  // AND persist the disposition as 'implemented'. Mirrors rowAccept.
  async function aiApply(p, sugg) {
    const ccmSuggestion = normalizeCcmSuggestionForDisposition(sugg);
    if (!ccmSuggestion) {
      setError('AI suggestion is not a CCM enrollment recommendation.');
      return;
    }
    const label = formatCcmSuggestionLabel({
      program: ccmSuggestion.recommendedCode,
      recommendedCode: ccmSuggestion.recommendedCode,
      label: ccmSuggestion.recommendedLabel,
      recommendedLabel: ccmSuggestion.recommendedLabel,
    });
    const tierNote2 = CCM_TIER_ENROLL_NORMALIZE[ccmSuggestion.recommendedCode]
      ? 'Enrolls at CCM base (99490) — the billed tier is decided monthly by documented time + MDM.'
      : '';
    if (!(await confirmDialog({
      title: `Enroll ${p.name} in ${label}?`,
      body: tierNote2 || undefined,
      bullets: [ATTEST_VERBAL],
      confirmLabel: 'Enroll',
    }))) return;
    setBusy(true); setError(null);
    try {
      // Enroll
      let consentDate = new Date().toISOString().slice(0, 10);
      if (p.raw.elation_id && p.consent !== 'signed') {
        try {
          const res = await ccmApi.firstVisitDates([String(p.raw.elation_id)]);
          consentDate = (res.results && res.results[String(p.raw.elation_id)]) || consentDate;
        } catch (_) {}
      }
      const raw = { ...p.raw };
      const prevConsent = p.raw.consent || p.consent || 'pending';
      applyEnrollment(raw, ccmSuggestion.recommendedCode, consentDate);
      try {
        await ccmApi.updatePatient(p.id, raw);
        if (prevConsent !== 'signed' && raw.consent === 'signed') {
          await auditConsentChange({ ...p, _raw: p.raw }, prevConsent, 'signed', {
            mode: 'AI panel suggestion verbal',
            date: consentDate,
          });
        }
      } catch (err) {
        if (err && err.status === 409) {
          flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`);
          await refresh();
          return;
        }
        throw err;
      }

      // Mark disposition implemented
      await ccmApi.upsertDisposition({
        patient_id: p.id,
        suggestion_hash: suggestionHash(ccmSuggestion.programFamily, ccmSuggestion.recommendedCode),
        program_slot: 'ccm',
        program_family: ccmSuggestion.programFamily,
        recommended_code: ccmSuggestion.recommendedCode,
        status: 'implemented',
        review_month: month,
        suggestion_payload: ccmSuggestion,
      });

      flashToast(`Enrolled ${p.name} in ${label}`);
      setDispositions((prev) => {
        const prior = prev[p.id] || { ccm: null, bhi: null };
        return { ...prev, [p.id]: { ...prior, ccm: { ...(prior.ccm || {}), status: 'implemented' } } };
      });
      await refresh();
    } catch (e) {
      setError(e?.message || 'Apply failed');
    } finally {
      setBusy(false);
    }
  }

  async function aiDismiss(p) {
    setBusy(true); setError(null);
    try {
      const rawSugg = (p._aiReview && p._aiReview.suggestion_payload) || {};
      const sugg = normalizeCcmSuggestionForDisposition(rawSugg) || rawSugg;
      await ccmApi.upsertDisposition({
        patient_id: p.id,
        suggestion_hash: suggestionHash(sugg.programFamily, sugg.recommendedCode),
        program_slot: 'ccm',
        program_family: sugg.programFamily,
        recommended_code: sugg.recommendedCode,
        status: 'dismissed',
        review_month: month,
        suggestion_payload: sugg,
      });
      setDispositions((prev) => {
        const prior = prev[p.id] || { ccm: null, bhi: null };
        return { ...prev, [p.id]: { ...prior, ccm: { ...(prior.ccm || {}), status: 'dismissed' } } };
      });
      flashToast(`Dismissed AI suggestion for ${p.name}`);
    } catch (e) {
      setError(e?.message || 'Dismiss failed');
    } finally {
      setBusy(false);
    }
  }

  async function aiSendToProvider(p) {
    setBusy(true); setError(null);
    try {
      const rawSugg = (p._aiReview && p._aiReview.suggestion_payload) || {};
      const sugg = normalizeCcmSuggestionForDisposition(rawSugg) || rawSugg;
      await ccmApi.upsertDisposition({
        patient_id: p.id,
        suggestion_hash: suggestionHash(sugg.programFamily, sugg.recommendedCode),
        program_slot: 'ccm',
        program_family: sugg.programFamily,
        recommended_code: sugg.recommendedCode,
        status: 'sent_to_provider',
        review_month: month,
        suggestion_payload: sugg,
      });
      setDispositions((prev) => {
        const prior = prev[p.id] || { ccm: null, bhi: null };
        return { ...prev, [p.id]: { ...prior, ccm: { ...(prior.ccm || {}), status: 'sent_to_provider' } } };
      });
      flashToast(`Sent ${p.name} to provider`);
    } catch (e) {
      setError(e?.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  }

  // BHI-slot counterparts to aiApply / aiDismiss / aiSendToProvider. Same
  // shape as the CCM path: write the patient blob (when applying, this sets
  // billing.bhiProgram + bhi.dx + bhi.enrolledAt), upsert the disposition under
  // the bhi slot, and merge dispositions[p.id].bhi locally so the row updates
  // without a full refresh. The Time Logs → BHI view captures the initial
  // assessment before the patient becomes actually billable.
  async function aiBhiApply(p, sugg) {
    const recommendedCode = String(sugg?.recommendedCode || '99484').trim() || '99484';
    if (!(await confirmDialog({
      title: `Enroll ${p.name} in BHI (${recommendedCode})?`,
      bullets: [ATTEST_VERBAL],
      confirmLabel: 'Enroll',
    }))) return;
    setBusy(true); setError(null);
    try {
      const reasons = Array.isArray(sugg?.reasons) ? sugg.reasons : [];
      const dx = (reasons[0] || p.raw.bhi?.dx || '').toString();
      const raw = { ...p.raw };
      const prevBhiConsent = p.raw.bhiConsent || null;
      let consentDate = new Date().toISOString().slice(0, 10);
      let dateFromFirstVisit = false;
      if (p.raw.elation_id && (!prevBhiConsent || prevBhiConsent.signed !== true)) {
        try {
          const res = await ccmApi.firstVisitDates([String(p.raw.elation_id)]);
          const fv = res.results && res.results[String(p.raw.elation_id)];
          if (fv) { consentDate = fv; dateFromFirstVisit = true; }
        } catch (_) {}
      }
      applyBhiEnrollment(raw, recommendedCode, consentDate);
      if (dx) raw.bhi.dx = dx;

      try {
        await ccmApi.updatePatient(p.id, raw);
        if ((!prevBhiConsent || prevBhiConsent.signed !== true) && raw.bhiConsent.signed === true) {
          await auditBhiConsentChange({ ...p, _raw: p.raw }, prevBhiConsent, raw.bhiConsent, {
            mode: 'BHI AI panel suggestion verbal',
            date: consentDate,
            dateSource: dateFromFirstVisit ? 'first visit' : 'today',
            source: 'verbal',
          });
        }
      } catch (err) {
        if (err && err.status === 409) {
          flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`);
          await refresh();
          return;
        }
        throw err;
      }

      const programFamily = String(sugg?.programFamily || 'BHI');
      await ccmApi.upsertDisposition({
        patient_id: p.id,
        suggestion_hash: suggestionHash(programFamily, recommendedCode),
        program_slot: 'bhi',
        program_family: programFamily,
        recommended_code: recommendedCode,
        status: 'implemented',
        review_month: month,
        suggestion_payload: sugg,
      });

      flashToast(`Enrolled ${p.name} in ${formatBhiSuggestionLabel({ recommendedCode })}`);
      setDispositions((prev) => {
        const prior = prev[p.id] || { ccm: null, bhi: null };
        return { ...prev, [p.id]: { ...prior, bhi: { ...(prior.bhi || {}), status: 'implemented' } } };
      });
      await refresh();
    } catch (e) {
      setError(e?.message || 'BHI Accept failed');
    } finally {
      setBusy(false);
    }
  }

  async function aiBhiDismiss(p) {
    setBusy(true); setError(null);
    try {
      const sugg = (p._aiBhiReview && p._aiBhiReview.suggestion_payload) || {};
      const programFamily = String(sugg.programFamily || 'BHI');
      const recommendedCode = String(sugg.recommendedCode || '99484').trim() || '99484';
      await ccmApi.upsertDisposition({
        patient_id: p.id,
        suggestion_hash: suggestionHash(programFamily, recommendedCode),
        program_slot: 'bhi',
        program_family: programFamily,
        recommended_code: recommendedCode,
        status: 'dismissed',
        review_month: month,
        suggestion_payload: sugg,
      });
      setDispositions((prev) => {
        const prior = prev[p.id] || { ccm: null, bhi: null };
        return { ...prev, [p.id]: { ...prior, bhi: { ...(prior.bhi || {}), status: 'dismissed' } } };
      });
      flashToast(`Dismissed BHI suggestion for ${p.name}`);
    } catch (e) {
      setError(e?.message || 'Dismiss failed');
    } finally {
      setBusy(false);
    }
  }

  async function aiBhiSendToProvider(p) {
    setBusy(true); setError(null);
    try {
      const sugg = (p._aiBhiReview && p._aiBhiReview.suggestion_payload) || {};
      const programFamily = String(sugg.programFamily || 'BHI');
      const recommendedCode = String(sugg.recommendedCode || '99484').trim() || '99484';
      await ccmApi.upsertDisposition({
        patient_id: p.id,
        suggestion_hash: suggestionHash(programFamily, recommendedCode),
        program_slot: 'bhi',
        program_family: programFamily,
        recommended_code: recommendedCode,
        status: 'sent_to_provider',
        review_month: month,
        suggestion_payload: sugg,
      });
      setDispositions((prev) => {
        const prior = prev[p.id] || { ccm: null, bhi: null };
        return { ...prev, [p.id]: { ...prior, bhi: { ...(prior.bhi || {}), status: 'sent_to_provider' } } };
      });
      flashToast(`Sent ${p.name} to provider (BHI)`);
    } catch (e) {
      setError(e?.message || 'Send failed');
    } finally {
      setBusy(false);
    }
  }

  // Refresh the row's BHI consent + enrollment state after the pill writes
  // back. The pill PUTs the patient blob then calls onSaved(nextRaw), so
  // re-running adapt against nextRaw gives us the canonical local row.
  function patchLocalPatient(id, nextRaw) {
    setPatients((prev) => prev.map((row) => {
      if (row.id !== id) return row;
      const adapted = adapt({ id, data: nextRaw }, bhiSettings || {});
      return adapted || row;
    }));
  }

  // Pain-enrollment badge + (when the CPM record can't yet bill) a warning chip
  // and a button that reopens the 8a dialog to finish it. Shared by the
  // prospect list and the enrolled section so the two never drift.
  function renderPainEnrollment(p) {
    if (!(p.raw.billing?.painProgram && p.raw.billing.painProgram !== 'none')) return null;
    const incomplete = !isPainRecordComplete(p.raw);
    return (
      <div className="ri-meta onboard-suggest" onClick={(e) => e.stopPropagation()}>
        <span className="badge">Pain {p.raw.billing.painProgram}</span>
        {incomplete && (
          <span className="badge warn" title="G3002 won't bill until the CPM consent and the initiating in-person visit (date + practitioner) are recorded.">
            CPM record incomplete — G3002 won't bill
          </span>
        )}
        {/* Reachable for COMPLETE records too: the necessity and excluded-
            treatments texts are living record fields (P1.3/P2.1) — hiding the
            dialog after completion made them unmaintainable (adversarial
            review finding 7). */}
        <button
          className="btn ghost sm"
          disabled={busy}
          title={incomplete
            ? 'Record the CPM consent + initiating in-person visit so G3002 can bill'
            : 'Edit the CPM record (necessity, excluded treatments, visit, practitioner)'}
          onClick={(e) => { e.stopPropagation(); rowEnrollPain(p); }}>
          {incomplete ? 'Complete CPM record' : 'Edit CPM record'}
        </button>
      </div>
    );
  }

  function renderPinEnrollment(p) {
    if (!(p.raw.billing?.navProgram && p.raw.billing.navProgram !== 'none')) return null;
    const incomplete = !isPinRecordComplete(p.raw);
    return (
      <div className="ri-meta onboard-suggest" onClick={(e) => e.stopPropagation()}>
        <span className="badge">PIN G0023</span>
        {incomplete && (
          <>
            <span className="badge warn" title="PIN consent, qualifying condition, and initiating visit must be recorded before G0023 can bill.">
              PIN record incomplete — G0023 won't bill
            </span>
            <button
              className="btn ghost sm"
              disabled={busy}
              title="Record PIN consent + qualifying condition + initiating visit so G0023 can bill"
              onClick={(e) => { e.stopPropagation(); rowEnrollPin(p); }}>
              Complete PIN record
            </button>
          </>
        )}
      </div>
    );
  }

  // Re-offer consent: declined → pending, so consent can be captured again
  // (verbally or by link). The prior decline reason is preserved on the record
  // (consentReoffer.previousReason) and the change is audit-logged with the
  // operator's reason. This is deliberately per-patient with a required
  // reason — bulk flows never touch declined patients.
  async function reofferConsent(p, reason) {
    setBusy(true); setError(null);
    try {
      const nextRaw = {
        ...p.raw,
        consent: 'pending',
        consentReoffer: {
          date: new Date().toISOString().slice(0, 10),
          reason,
          previousReason: p.raw.consentDeclinedReason || null,
        },
      };
      delete nextRaw.consentDeclinedReason;
      clearConsentLinkProvenance(nextRaw);
      await ccmApi.updatePatient(p.id, nextRaw);
      await auditConsentChange(
        { id: p.id, fname: p.raw.fname, lname: p.raw.lname, mrn: p.raw.mrn },
        'declined', 'pending', { mode: 're-offer', reason },
      );
      patchLocalPatient(p.id, nextRaw);
      flashToast(`Consent re-offered for ${p.name}`);
      setReofferPatient(null);
    } catch (err) {
      if (err && err.status === 409) {
        flashToast('Patient was updated elsewhere. Reloaded latest data; try again.');
        setReofferPatient(null);
        await refresh();
      } else {
        setError(err?.message || 'Re-offer failed');
      }
    } finally {
      setBusy(false);
    }
  }

  // Programs a fresh consent link would enroll for this row — the mint
  // snapshot. CCM rides for ANY unenrolled consent-pending patient (no AI
  // suggestion required): the standing code is always base 99490, because the
  // monthly tier (99487/99491 bump, APCM fallback) stays a staff decision and
  // 99490 + stacked 99439 add-ons is the revenue-max code below the 90-minute
  // bump threshold anyway. BHI rides only with a 99484 eligibility signal —
  // link enrollment attests the same clinical basis as the Accept · BHI button.
  function linkProgramsFor(p) {
    const programs = [];
    if (p.consent === 'pending' && isUnenrolledCcm(p)) programs.push('ccm');
    if (bhiEnabled && isUnenrolledBhi(p) && !(p.raw && p.raw.bhi && p.raw.bhi.declined)) {
      const bhiConsentSigned = !!(p.raw && p.raw.bhiConsent && p.raw.bhiConsent.signed === true);
      const consentOpen = combinedConsent ? p.consent === 'pending' : !bhiConsentSigned;
      const bhiSugg = actionableBhiSuggestion(p);
      if (consentOpen && bhiSugg && String(bhiSugg.recommendedCode || '') === '99484') programs.push('bhi');
    }
    return programs;
  }

  // Inline secondary consent path: mint a link for this row and show the
  // one-time fresh-link card (copy / QR / SMS text). Verbal attestation via
  // Accept/enroll remains the primary path and is untouched. Approval
  // auto-enrolls the snapshotted programs.
  async function rowSendConsentLink(p, programs) {
    setBusy(true); setError(null);
    try {
      const r = await createConsentLink(p.id, '', programs);
      setConsentFresh({ ...r, patientName: p.name });
      setConsentReloadKey((k) => k + 1); // panel list refresh, if open
    } catch (e) {
      setError(e?.message || 'Could not create the consent link.');
    } finally { setBusy(false); }
  }

  function normalizedEnrollmentCode(code) {
    return isEnrolledProgram(code) ? String(code).trim().toUpperCase() : '';
  }

  function renderProgramBadge(programKey, code, isCurrent = false) {
    const key = programKey === 'bhi' ? 'bhi' : 'ccm';
    const label = key === 'bhi' ? 'BHI' : 'CCM';
    const normalizedCode = normalizedEnrollmentCode(code);
    const enrolled = !!normalizedCode;
    return (
      <span
        key={key}
        className={`badge prog-badge prog-badge-${key} ${enrolled ? 'enrolled' : 'unenrolled'}${isCurrent ? ' is-current' : ''}`}
        title={`${label} enrollment status: ${enrolled ? `enrolled in ${normalizedCode}` : 'not enrolled'}`}>
        {enrolled ? `${label} enrolled · ${normalizedCode}` : `${label} not enrolled`}
      </span>
    );
  }

  // Per-program enrollment status badges for a row. Renders nothing when
  // bhi_enabled is false (preserves today's UI exactly for practices that
  // haven't turned BHI on). The badge for the currently-filtered program
  // gets an `is-current` marker for bolded emphasis.
  function renderProgramBadges(p) {
    if (!bhiEnabled) return null;
    const ccmCode = p.raw?.billing?.program || p.program || '';
    const bhiCode = (p.raw && p.raw.billing && p.raw.billing.bhiProgram) || '';
    const ccmBadge = renderProgramBadge('ccm', ccmCode, programFilter === 'ccm');
    const bhiBadge = renderProgramBadge('bhi', bhiCode, programFilter === 'bhi');
    // Lead with the badge matching the active chrome filter so the lens
    // patient state reads naturally in BHI view.
    return programFilter === 'bhi' ? <>{bhiBadge}{ccmBadge}</> : <>{ccmBadge}{bhiBadge}</>;
  }

  return (
    <section className="onboard-page" role="region" aria-label="Onboard patients">
        <header className="onboard-head">
          <div>
            <h2>Onboard patients</h2>
            <div className="onboard-sub">
              {programFilter === 'all' && bhiEnabled
                ? <>Enroll unenrolled patients in CCM and/or BHI, or decline. Use the enrolled subsection to remove care-management enrollment when needed.</>
                : programFilter === 'bhi' && bhiEnabled
                ? <>Enroll unenrolled patients in BHI or decline. Use the enrolled subsection to remove BHI enrollment when needed.</>
                : <>Enroll unenrolled patients in CCM or decline. Use the enrolled subsection to remove care-management enrollment when needed.</>}
              {' '}APCM is now a per-month billing fallback — see the 3 · APCM Fallback tab.
            </div>
          </div>
        </header>

        {consentLinksEnabled && (
          <div className="onboard-consent-links">
            <button
              className="btn ghost sm"
              aria-expanded={consentLinksOpen}
              onClick={() => setConsentLinksOpen((o) => !o)}>
              {consentLinksOpen ? '▾ Hide consent links' : '▸ Consent links (send to patient)'}
            </button>
            {consentLinksOpen && (
              <ConsentLinksPanel
                patients={patients}
                practiceName={bhiSettings?.practiceSettings?.practiceName}
                reloadKey={consentReloadKey}
                linkProgramsFor={linkProgramsFor}
              />
            )}
          </div>
        )}

        <div className="onboard-toolbar">
          <input
            type="search"
            className="onboard-search"
            placeholder="Search name or MRN…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <label className="onboard-toggle" title="CCM requires ≥2 chronic conditions per CMS; below-threshold patients are hidden by default">
            <input type="checkbox" checked={showBelowThreshold} onChange={(e) => setShowBelowThreshold(e.target.checked)} />
            Show &lt;2 chronic
          </label>
          <label className="onboard-toggle">
            <input type="checkbox" checked={showDeclined} onChange={(e) => setShowDeclined(e.target.checked)} />
            Show declined
          </label>
          <label className="onboard-toggle" title="Patients the Elation sync marked out-of-workflow are hidden by default; reveal them here (rows carry an Excluded badge)">
            <input type="checkbox" checked={showExcluded} onChange={(e) => setShowExcluded(e.target.checked)} />
            Show excluded
          </label>
        </div>

        {programFilter !== 'bhi' && (
          <div className="onboard-tag-row" role="group" aria-label="Consent filters">
            <span className="onboard-tag-label">Consent:</span>
            <button
              className={`onboard-pill ${consentLens === 'pending' ? 'active' : ''}`}
              title="Show only patients whose CCM consent is still pending"
              onClick={() => setConsentLens(consentLens === 'pending' ? null : 'pending')}>
              pending <span className="tabnum">{consentCounts.pending}</span>
            </button>
            <button
              className={`onboard-pill ${consentLens === 'declined' ? 'active' : ''}`}
              title="Declined patients are hidden by default — this lens shows only them (re-offer consent from the row)"
              onClick={() => setConsentLens(consentLens === 'declined' ? null : 'declined')}>
              declined <span className="tabnum">{consentCounts.declined}</span>
            </button>
          </div>
        )}

        <div className="onboard-tag-row" role="group" aria-label="Insurance filters">
          <span className="onboard-tag-label">Insurance:</span>
          <button
            className={`onboard-pill ${payerLens === 'medicare' ? 'active' : ''}`}
            title="Medicare FFS, Medicare Advantage, and dual-eligible patients"
            onClick={() => setPayerLens(payerLens === 'medicare' ? null : 'medicare')}>
            Medicare / MA <span className="tabnum">{payerCounts.medicare}</span>
          </button>
          <button
            className={`onboard-pill ${payerLens === 'commercial' ? 'active' : ''}`}
            title="Commercial coverage (many plans reimburse CCM CPTs)"
            onClick={() => setPayerLens(payerLens === 'commercial' ? null : 'commercial')}>
            Commercial <span className="tabnum">{payerCounts.commercial}</span>
          </button>
          <button
            className={`onboard-pill ${payerLens === 'medicaid' ? 'active' : ''}`}
            title="Medicaid-only patients (duals count under Medicare / MA)"
            onClick={() => setPayerLens(payerLens === 'medicaid' ? null : 'medicaid')}>
            Medicaid only <span className="tabnum">{payerCounts.medicaid}</span>
          </button>
          {payerCounts.other > 0 && (
            <button
              className={`onboard-pill ${payerLens === 'other' ? 'active' : ''}`}
              title="Unclassified or unknown payers"
              onClick={() => setPayerLens(payerLens === 'other' ? null : 'other')}>
              Other / Unknown <span className="tabnum">{payerCounts.other}</span>
            </button>
          )}
          <button
            className={`onboard-pill ${age65 ? 'active' : ''}`}
            title="Only patients aged 65 and over (composes with the insurance lens)"
            onClick={() => setAge65(!age65)}>
            65+ <span className="tabnum">{payerCounts.age65}</span>
          </button>
        </div>

        {payerLens === 'commercial' && carrierCounts && carrierCounts.length > 0 && (
          <div className="onboard-tag-row" role="group" aria-label="Carrier filters">
            <span className="onboard-tag-label">Carrier:</span>
            {carrierCounts.map(([name, n]) => (
              <button
                key={name}
                className={`onboard-pill ${carrierLens === name ? 'active' : ''}`}
                title="Primary insurance carrier from Elation — commercial CCM coverage is carrier-specific, so pilot one carrier at a time"
                onClick={() => setCarrierLens(carrierLens === name ? null : name)}>
                {name} <span className="tabnum">{n}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mobile-workflow-summary onboard-mobile-summary" aria-label="Onboarding queue summary">
          <div><span>Unenrolled</span><b className="tabnum">{visible.length}</b></div>
          <div><span>Selected</span><b className="tabnum">{selectedCount}</b></div>
          <div><span>Enrolled</span><b className="tabnum">{enrolledVisible.length}</b></div>
        </div>

        <div className="onboard-primary-section">
          {selectedCount > 0 && (
            <div className="bulk-bar onboard-bulk">
              <span><b>{selectedCount}</b> unenrolled selected</span>
              <span className="bulk-spacer" />
              <button
                className="btn sm"
                disabled={busy || selectedRuleSuggestionCount === 0}
                title={selectedRuleSuggestionCount > 0
                  ? 'Apply visible rule-based CCM/BHI suggestions on the selected rows.'
                  : 'No selected rows have a visible rule-based suggestion to accept.'}
                onClick={bulkAcceptRuleSuggestions}>
                Accept rule suggestions ({selectedRuleSuggestionCount})
              </button>
              <button
                className="btn sm"
                disabled={busy || selectedLlmSuggestionCount === 0}
                title={selectedLlmSuggestionCount > 0
                  ? 'Apply pending LLM-reviewed recommendations already saved on the selected rows.'
                  : 'No selected rows have pending LLM-reviewed recommendations yet. Run LLM review first.'}
                onClick={bulkAcceptLlmSuggestions}>
                Accept LLM-reviewed ({selectedLlmSuggestionCount})
              </button>
              <button
                className="btn sm"
                disabled={busy}
                title="Run the LLM panel review for selected patients and save per-row CCM/BHI suggestions. Rule-based row suggestions are already visible; this does not enroll anyone."
                onClick={bulkAIReview}>
                {busy ? '✨ LLM reviewing…' : `✨ Run LLM review (${selectedCount})`}
              </button>
              {(programFilter === 'ccm' || programFilter === 'all') && (
                <div className="split-btn" ref={tierMenuRef}>
                  <button
                    className="btn sm"
                    disabled={busy}
                    title="Enrolls at the CCM base (99490). The billed tier is decided monthly: a ≥90-minute month offers the 99487/99491 bump with operator MDM or physician-time affirmation."
                    onClick={() => { setTierMenuOpen(false); bulkEnroll('99490'); }}>
                    Enroll {selectedCount} in CCM
                  </button>
                  <button
                    className="btn sm split-btn-caret"
                    disabled={busy}
                    aria-haspopup="menu"
                    aria-expanded={tierMenuOpen}
                    aria-label="Other program options"
                    onClick={() => setTierMenuOpen(o => !o)}>▾</button>
                  {tierMenuOpen && (
                    <div className="split-btn-menu" role="menu">
                      <button role="menuitem" disabled={busy}
                              title="Principal Care Management: ONE complex condition (≥3 months, high risk of hospitalization/decline). Note generation requires an unambiguous principal condition — exactly one documented condition, or billing.pcmCondition set to one of them."
                              onClick={() => { setTierMenuOpen(false); bulkEnroll('99426'); }}>
                        PCM 99426 — Staff, 30 min · single condition
                      </button>
                      <button role="menuitem" disabled={busy}
                              title="Practitioner-personally-furnished PCM. Same single-condition requirements as 99426."
                              onClick={() => { setTierMenuOpen(false); bulkEnroll('99424'); }}>
                        PCM 99424 — Physician, 30 min · single condition
                      </button>
                    </div>
                  )}
                </div>
              )}
              {programFilter === 'pain' && painEnabled && (
                <button className="btn sm" disabled={busy} onClick={bulkEnrollPain}>
                  Enroll {selectedCount} in Pain Mgmt
                </button>
              )}
              {(programFilter === 'bhi' || (programFilter === 'all' && bhiEnabled)) && (
                <button className="btn sm" disabled={busy} onClick={bulkEnrollBhi}>
                  Enroll {selectedCount} in BHI
                </button>
              )}
              <button className="btn sm" disabled={busy} onClick={() => setDeclineOpen(true)}>
                {programFilter === 'bhi' ? 'Mark BHI declined…' : 'Mark declined…'}
              </button>
              <button className="btn ghost sm" disabled={busy} onClick={() => setSelected(new Set())}>Clear</button>
            </div>
          )}

          <div className="onboard-list-head onboard-unenrolled-head">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={visible.length === 0} />
              Select all unenrolled
            </label>
            <span
              className="onboard-count tabnum"
              title={programFilter === 'all' && bhiEnabled ? 'Patients unenrolled in CCM, BHI, or both.' : undefined}
            >
              {visible.length} unenrolled
            </span>
          </div>

          <div className="onboard-list onboard-unenrolled-list">
            {loading && (
              <div role="status" aria-live="polite" aria-label="Loading patients">
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={`sk${i}`} className="onboard-row-skeleton skeleton">
                    <div className="sk-avatar" />
                    <div style={{ flex: 1 }}><div className="sk-line w40" /><div className="sk-line w70" /></div>
                  </div>
                ))}
              </div>
            )}
            {!loading && error && (
              <div className="onboard-empty" style={{ color: 'var(--bad)' }}>{error}</div>
            )}
            {!loading && !error && visibleWithAI.length === 0 && (
              <div className="onboard-empty">
                {query ? `No unenrolled patients match "${query}".` : 'No unenrolled patients to onboard.'}
              </div>
            )}
            {!loading && visibleWithAI.map((p, idx) => {
              const isCheck = selected.has(p.id);
              const cantAccept = p.suggestion.program === 'none';
              return (
                <div
                  key={p.id}
                  className={`roster-item onboard-row clickable ${isCheck ? 'is-selected' : ''}`}
                  onClick={(e) => rowClick(e, idx)}
                  onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isCheck}
                >
                  <input
                    type="checkbox"
                    checked={isCheck}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(p.id, idx)}
                  />
                  <div className={`avatar av-${p.avSlot}`}>{nameInitials(p.raw)}</div>
                  <div className="onboard-row-main">
                    <div className="ri-name">
                      {p.name}
                      {p.age != null && <span className="onboard-age"> · {p.age}{p.sex ? ` ${p.sex[0].toUpperCase()}` : ''}</span>}
                      {p.mrn && <span className="onboard-mrn"> · MRN {p.mrn}</span>}
                    </div>
                    <div className="ri-meta onboard-meta">
                      <span className="badge">{p.chronic} chronic dx</span>
                      {/* CCM consent badge is hidden in BHI view — BHI has its own
                          consent flow (BhiConsentPill) shown for BHI-enrolled rows. */}
                      <span
                        className="badge"
                        title={p.primaryCarrier ? `Primary carrier: ${p.primaryCarrier}` : 'Payer class derived from Elation insurance'}>
                        {PAYER_LABEL[p.payerClass] || p.payerClass}
                      </span>
                      {programFilter !== 'bhi' && p.needs.needsConsent && (
                        <span className="badge">Consent: {p.consent === 'declined' ? 'declined' : 'pending'}</span>
                      )}
                      {consentLinksEnabled && (() => {
                        const b = consentLinkBadge(consentLinkMap[p.id]);
                        return b ? (
                          <span className={`badge${b.tone === 'warn' ? ' warn' : ''}`} title="Patient consent-link status">{b.label}</span>
                        ) : null;
                      })()}
                      {p.workflowExcluded && (
                        <span className="badge warn" title="Marked out-of-workflow by the Elation sync">Excluded</span>
                      )}
                      {!p.raw.elation_id && <span className="badge warn">No Elation link</span>}
                      {renderProgramBadges(p)}
                    </div>
                    {(programFilter === 'ccm' || programFilter === 'all') && (
                      <SuggestionLine
                        p={p}
                        busy={busy}
                        onApply={aiApply}
                        onDismiss={aiDismiss}
                        onSendToProvider={aiSendToProvider}
                      />
                    )}
                    {programFilter === 'pain' && p.painSuggestion?.eligible && (
                      <div className="ri-meta onboard-suggest">
                        Pain signal: <span style={{ color: 'var(--ink-2)' }}>{p.painSuggestion.reasons.join(' · ')}</span>
                      </div>
                    )}
                    {bhiEnabled && (programFilter === 'bhi' || programFilter === 'all') && (
                      <BhiSuggestionLine
                        p={p}
                        busy={busy}
                        onApply={aiBhiApply}
                        onDismiss={aiBhiDismiss}
                        onSendToProvider={aiBhiSendToProvider}
                      />
                    )}
                    {renderPainEnrollment(p)}
                    {renderPinEnrollment(p)}
                    {p.raw.billing?.bhiProgram && (
                      <div className="ri-meta onboard-suggest bhi-enrollment-pills" onClick={(e) => e.stopPropagation()}>
                        <span className="badge">BHI {p.raw.billing.bhiProgram}</span>
                        {combinedConsent && p.consent === 'signed' ? (
                          <span className="bhi-pill ok" title="Combined CCM/BHI consent policy: CCM consent on file covers BHI">
                            <span className="bhi-pill-btn">BHI consent ✓ inherited</span>
                          </span>
                        ) : (
                          <BhiConsentPill
                            patient={{ ...p, _raw: p.raw }}
                            onSaved={(nextRaw) => patchLocalPatient(p.id, nextRaw)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="onboard-row-actions">
                    {(programFilter === 'ccm' || programFilter === 'all') && (
                      <button
                        className={`btn sm program-action-ccm ${cantAccept ? '' : 'primary'}`}
                        disabled={busy || cantAccept}
                        title={cantAccept ? 'AI suggests not enrolling — use Mark declined' : `Enroll in ${formatCcmSuggestionLabel(p.suggestion)}`}
                        onClick={(e) => { e.stopPropagation(); rowAccept(p); }}>
                        {cantAccept ? '—' : `Accept · ${formatCcmSuggestionLabel(p.suggestion)}`}
                      </button>
                    )}
                    {programFilter === 'pain' && (
                      <button
                        className="btn sm primary"
                        disabled={busy}
                        title="Enroll in Chronic Pain Management (G3002). Attests an in-person ≥30-min initiating visit by the billing practitioner and that monthly CPM time is practitioner/QHP time."
                        onClick={(e) => { e.stopPropagation(); rowEnrollPain(p); }}>
                        Enroll · Pain G3002
                      </button>
                    )}
                    {programFilter === 'pin' && (
                      <button
                        className="btn sm primary"
                        disabled={busy}
                        title="Enroll in Principal Illness Navigation (G0023) with consent, qualifying serious high-risk condition, initiating visit, and navigator-training attestation."
                        onClick={(e) => { e.stopPropagation(); rowEnrollPin(p); }}>
                        Enroll · PIN G0023
                      </button>
                    )}
                    {(programFilter === 'bhi' || programFilter === 'all') && (() => {
                      const bhiSugg = actionableBhiSuggestion(p);
                      const hasBhi = !!(bhiSugg && bhiSugg.recommendedCode && bhiSugg.recommendedCode !== 'none');
                      if (!hasBhi) return null;
                      return (
                        <button
                          className="btn sm primary program-action-bhi"
                          disabled={busy}
                          title={`Enroll in ${formatBhiSuggestionLabel(bhiSugg)}`}
                          onClick={(e) => { e.stopPropagation(); aiBhiApply(p, bhiSugg); }}>
                          Accept · {formatBhiSuggestionLabel(bhiSugg)}
                        </button>
                      );
                    })()}
                    {consentLinksEnabled && (() => {
                      const linkProgs = linkProgramsFor(p);
                      if (linkProgs.length === 0) return null;
                      const labels = linkProgs.map((x) => (x === 'bhi' ? 'BHI 99484' : 'CCM 99490')).join(' + ');
                      return (
                        <button
                          className="btn ghost sm"
                          disabled={busy}
                          title={`Send consent link — the patient reviews the program and approves on their phone; approval enrolls ${labels} automatically`}
                          onClick={(e) => { e.stopPropagation(); rowSendConsentLink(p, linkProgs); }}>
                          🔗 Consent
                        </button>
                      );
                    })()}
                    {programFilter !== 'bhi' && p.consent === 'declined' && (
                      <button
                        className="btn ghost sm"
                        disabled={busy}
                        title="Re-offer consent — moves the patient from declined back to consent pending (reason required, audit-logged)"
                        onClick={(e) => { e.stopPropagation(); setReofferPatient(p); }}>
                        Re-offer consent
                      </button>
                    )}
                    <button
                      className="btn ghost sm"
                      disabled={busy}
                      title="Edit patient"
                      onClick={(e) => { e.stopPropagation(); setEditPatient(p); }}>
                      ✎
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <section className={`onboard-enrolled-section ${enrolledOpen ? 'is-open' : 'is-collapsed'}`} aria-label="Currently enrolled patients">
          <button
            type="button"
            className="onboard-enrolled-toggle"
            aria-expanded={enrolledOpen}
            onClick={() => setEnrolledOpen(o => !o)}
          >
            <span className="onboard-accordion-left">
              <span className={`onboard-accordion-chevron ${enrolledOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
              <span className="onboard-accordion-main">
                <span className="onboard-accordion-title">Currently enrolled</span>
                <span className="onboard-accordion-sub">Unenroll or inspect active care-management patients</span>
              </span>
            </span>
            <span className="onboard-enrolled-meta">
              {selEnrolledPatients.length > 0 && (
                <span className="onboard-selected-count tabnum">{selEnrolledPatients.length} selected</span>
              )}
              <span className="onboard-enrolled-count tabnum">{enrolledVisible.length} enrolled</span>
            </span>
          </button>

          {enrolledOpen && (
            <div className="onboard-enrolled-body">
              {selEnrolledPatients.length > 0 && (
                <div className="bulk-bar onboard-bulk onboard-enrolled-bulk">
                  <span><b>{selEnrolledPatients.length}</b> enrolled selected</span>
                  <span className="bulk-spacer" />
                  <button
                    className="btn danger sm"
                    disabled={busy}
                    onClick={() => setUnenrollOpen({ patients: selEnrolledPatients })}>
                    Unenroll…
                  </button>
                  <button className="btn ghost sm" disabled={busy} onClick={() => setSelectedEnrolled(new Set())}>Clear</button>
                </div>
              )}

              <div className="onboard-list-head onboard-enrolled-head">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)' }}>
                  <input type="checkbox" checked={allEnrolledSelected} onChange={toggleAllEnrolled} disabled={enrolledVisible.length === 0} />
                  Select all enrolled
                </label>
                <span className="onboard-count tabnum">{enrolledVisible.length} enrolled</span>
              </div>

              <div className="onboard-list onboard-enrolled-list">
                {loading && (
                  <div role="status" aria-live="polite" aria-label="Loading enrolled patients">
                    {Array.from({ length: 3 }, (_, i) => (
                      <div key={`esk${i}`} className="onboard-row-skeleton skeleton">
                        <div className="sk-avatar" />
                        <div style={{ flex: 1 }}><div className="sk-line w40" /><div className="sk-line w70" /></div>
                      </div>
                    ))}
                  </div>
                )}
                {!loading && !error && enrolledVisible.length === 0 && (
                  <div className="onboard-empty onboard-enrolled-empty">
                    {query ? `No enrolled patients match "${query}".` : 'No enrolled patients.'}
                  </div>
                )}
                {!loading && enrolledVisible.map((p) => {
                  const isCheck = selectedEnrolled.has(p.id);
                  const apcmDecision = month && p.raw?.apcmMonth?.[month];
                  return (
                    <div
                      key={p.id}
                      className={`roster-item onboard-row onboard-enrolled-row clickable ${isCheck ? 'is-selected' : ''}`}
                      onClick={() => toggleEnrolled(p.id)}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isCheck}
                    >
                      <input
                        type="checkbox"
                        checked={isCheck}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleEnrolled(p.id)}
                      />
                      <div className={`avatar av-${p.avSlot}`}>{nameInitials(p.raw)}</div>
                      <div className="onboard-row-main">
                        <div className="ri-name">
                          {p.name}
                          {p.age != null && <span className="onboard-age"> · {p.age}{p.sex ? ` ${p.sex[0].toUpperCase()}` : ''}</span>}
                          {p.mrn && <span className="onboard-mrn"> · MRN {p.mrn}</span>}
                        </div>
                        <div className="ri-meta onboard-meta">
                          {renderProgramBadges(p)}
                          <span className="badge">{p.chronic} chronic dx</span>
                          {p.consent !== 'signed' && <span className="badge warn">Consent: {p.consent}</span>}
                          {apcmDecision?.code && <span className="badge">APCM {apcmDecision.code}</span>}
                          {!p.raw.elation_id && <span className="badge warn">No Elation link</span>}
                        </div>
                        {/* BHI consent is editable here too — a patient enrolled in both
                            programs only appears in this section, and the pill is the sole
                            UI that can correct a bhiConsent date. */}
                        {renderPainEnrollment(p)}
                        {renderPinEnrollment(p)}
                    {p.raw.billing?.bhiProgram && (
                          <div className="ri-meta onboard-suggest bhi-enrollment-pills" onClick={(e) => e.stopPropagation()}>
                            {combinedConsent && p.consent === 'signed' ? (
                              <span className="bhi-pill ok" title="Combined CCM/BHI consent policy: CCM consent on file covers BHI">
                                <span className="bhi-pill-btn">BHI consent ✓ inherited</span>
                              </span>
                            ) : (
                              <BhiConsentPill
                                patient={{ ...p, _raw: p.raw }}
                                onSaved={(nextRaw) => patchLocalPatient(p.id, nextRaw)}
                              />
                            )}
                          </div>
                        )}
                      </div>
                      <div className="onboard-row-actions">
                        <button
                          className="btn danger sm"
                          disabled={busy}
                          title="Remove the primary care-management enrollment program"
                          onClick={(e) => { e.stopPropagation(); setUnenrollOpen({ patients: [p] }); }}>
                          Unenroll…
                        </button>
                        <button
                          className="btn ghost sm"
                          disabled={busy}
                          title="Edit patient"
                          onClick={(e) => { e.stopPropagation(); setEditPatient(p); }}>
                          ✎
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {toast && <div className="onboard-toast">{toast}</div>}

        {declineOpen && <DeclineModal
          count={selectedCount}
          busy={busy}
          programFilter={programFilter}
          bhiEnabled={bhiEnabled}
          onClose={() => setDeclineOpen(false)}
          onConfirm={(reason, mode) => bulkDecline(reason, mode)}
        />}

        {unenrollOpen && <UnenrollModal
          count={unenrollOpen.patients.length}
          busy={busy}
          programSlot={programFilter === 'bhi' ? 'bhi' : 'ccm'}
          onClose={() => setUnenrollOpen(null)}
          onConfirm={(reason) => unenrollPatients(unenrollOpen.patients, reason, programFilter === 'bhi' ? 'bhi' : 'ccm')}
        />}

        {editPatient && <EditPatientModal
          patient={editPatient}
          busy={busy}
          onClose={() => setEditPatient(null)}
          onSave={saveEdit}
        />}

        {pcmAssign && (
          <PcmConditionModal
            program={pcmAssign.program}
            rows={pcmAssign.rows}
            busy={busy}
            onChange={(idx, choice) => setPcmAssign((prev) => prev && ({
              ...prev,
              rows: prev.rows.map((r, i) => (i === idx ? { ...r, choice } : r)),
            }))}
            onClose={() => !busy && setPcmAssign(null)}
            onConfirm={confirmPcmEnroll}
          />
        )}
        {reofferPatient && <ReofferConsentModal
          patientName={reofferPatient.name}
          busy={busy}
          onClose={() => setReofferPatient(null)}
          onConfirm={(reason) => reofferConsent(reofferPatient, reason)}
        />}

        {consentFresh && (
          <div className="modal-backdrop" onClick={() => setConsentFresh(null)}>
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-label="Consent link created"
              onClick={(e) => e.stopPropagation()}>
              <div className="modal-head"><h3>Consent link for {consentFresh.patientName}</h3></div>
              <div className="modal-body">
                <ConsentFreshLink result={consentFresh} practiceName={bhiSettings?.practiceSettings?.practiceName} />
              </div>
              <div className="modal-foot">
                <button className="btn primary" onClick={() => setConsentFresh(null)}>Done</button>
              </div>
            </div>
          </div>
        )}
    </section>
  );
}

function SuggestionLine({ p, busy, onAccept, onApply, onDismiss, onSendToProvider }) {
  const aiReview = p._aiReview;
  if (!aiReview) {
    // Local heuristic (today's behavior).
    return (
      <>
        <div className="ri-meta onboard-suggest">
          AI: <b>{formatCcmSuggestionLabel(p.suggestion)}</b> — <span style={{ color: 'var(--ink-2)' }}>{p.suggestion.reason}</span>
        </div>
      </>
    );
  }

  if (aiReview.status === 'reviewing') {
    return (
      <div className="ri-meta onboard-suggest is-reviewing" aria-busy="true">
        AI ✨ Reviewing…
      </div>
    );
  }

  // Status: pending | implemented | dismissed | sent_to_provider — only render
  // actions on pending; the others are terminal states.
  const sugg = aiReview.suggestion_payload || {};
  const missing = Array.isArray(sugg.missingValidation) ? sugg.missingValidation : [];
  return (
    <>
      <div className="ri-meta onboard-suggest ai-upgraded">
        AI ✨ <b>{formatCcmSuggestionLabel(sugg)}</b>
        {sugg.confidence ? <> · {sugg.confidence} confidence</> : null}
        {sugg.reason ? <> · <span style={{ color: 'var(--ink-2)' }}>{sugg.reason}</span></> : null}
      </div>
      {missing.length > 0 && (
        <div className="ri-meta onboard-suggest ai-upgraded ai-missing">
          Missing: {missing.join('; ')}
        </div>
      )}
      <div className="ri-meta onboard-suggest ai-actions">
        {aiReview.status === 'pending' && (
          <>
            <button className="btn sm primary" disabled={busy} onClick={(e) => { e.stopPropagation(); onApply(p, sugg); }}>Apply</button>
            <button className="btn sm" disabled={busy} onClick={(e) => { e.stopPropagation(); onDismiss(p); }}>Dismiss</button>
            <button className="btn sm" disabled={busy} onClick={(e) => { e.stopPropagation(); onSendToProvider(p); }}>Send to provider</button>
          </>
        )}
        {aiReview.status !== 'pending' && (
          <span className="ai-disposition-state">AI review: {dispositionStatusLabel(aiReview.status)}</span>
        )}
      </div>
    </>
  );
}

// BhiSuggestionLine — parallel to SuggestionLine. Prefer persisted AI panel
// review state when present; otherwise show the local deterministic BHI rule so
// operators get useful BHI enrollment buttons before running an LLM review.
function BhiSuggestionLine({ p, busy, onApply, onDismiss, onSendToProvider }) {
  const aiReview = p._aiBhiReview;
  if (!aiReview) {
    const local = p.localBhiSuggestion;
    if (!local) return null;
    const scaleLabel = local.suggestedScale || 'PHQ-9';
    return (
      <>
        <div className="ri-meta onboard-suggest onboard-suggest-bhi local-rule">
          BHI: <b>{local.recommendedCode || '99484'}</b>
          {scaleLabel ? <> · {scaleLabel}</> : null}
          {local.reason ? <> · <span style={{ color: 'var(--ink-2)' }}>{local.reason}</span></> : null}
        </div>
        <div className="ri-meta onboard-suggest onboard-hint">
          After accepting, record the initial {scaleLabel} in Time Logs → BHI view before the patient can be billed.
        </div>
      </>
    );
  }

  if (aiReview.status === 'reviewing') {
    return (
      <div className="ri-meta onboard-suggest is-reviewing onboard-suggest-bhi" aria-busy="true">
        BHI ✨ Reviewing…
      </div>
    );
  }

  const sugg = aiReview.suggestion_payload || {};
  const scaleLabel = sugg.suggestedScale || 'PHQ-9';
  const missing = Array.isArray(sugg.missingValidation) ? sugg.missingValidation : [];
  return (
    <>
      <div className="ri-meta onboard-suggest ai-upgraded onboard-suggest-bhi">
        AI ✨ <b>{formatBhiSuggestionLabel(sugg)}</b>
        {sugg.confidence ? <> · {sugg.confidence} confidence</> : null}
        {sugg.reason ? <> · <span style={{ color: 'var(--ink-2)' }}>{sugg.reason}</span></> : null}
      </div>
      {missing.length > 0 && (
        <div className="ri-meta onboard-suggest ai-upgraded ai-missing">
          Missing: {missing.join('; ')}
        </div>
      )}
      <div className="ri-meta onboard-suggest ai-actions">
        {aiReview.status === 'pending' && (
          <>
            <button className="btn sm primary" disabled={busy} onClick={(e) => { e.stopPropagation(); onApply(p, sugg); }}>Accept · {formatBhiSuggestionLabel(sugg)}</button>
            <button className="btn sm" disabled={busy} onClick={(e) => { e.stopPropagation(); onDismiss(p); }}>Dismiss</button>
            <button className="btn sm" disabled={busy} onClick={(e) => { e.stopPropagation(); onSendToProvider(p); }}>Send to provider</button>
          </>
        )}
        {aiReview.status !== 'pending' && (
          <span className="ai-disposition-state">BHI review: {dispositionStatusLabel(aiReview.status)}</span>
        )}
      </div>
      {aiReview.status === 'pending' && (
        <div className="ri-meta onboard-suggest onboard-hint">
          After accepting, record the initial {scaleLabel} in Time Logs → BHI view before the patient can be billed.
        </div>
      )}
    </>
  );
}

// Re-offer consent (declined → pending). Reason is required and audit-logged;
// mirrors DeclineModal so the two consent-state transitions read as a pair.
// PCM condition assignment: one row per selected patient, choosing the single
// condition the enrollment manages. Single-condition charts render as fixed
// text (auto-assigned — nothing to decide); multi-condition charts get a
// required select. The Enroll click IS the attestation (one dialog total).
function PcmConditionModal({ program, rows, busy, onChange, onClose, onConfirm }) {
  const n = rows.length;
  const allChosen = rows.every((r) => r.choice);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="PCM principal condition"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}>
        <div className="modal-head"><h3>Enroll {n} patient{n !== 1 ? 's' : ''} in PCM {program}</h3></div>
        <div className="modal-body">
          <p>
            PCM manages <strong>one</strong> complex chronic condition per patient under a
            disease-specific care plan. Confirm which documented condition each enrollment is for —
            billing notes and the care plan are scoped to it.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '10px 0' }}>
            {rows.map((r, idx) => (
              <div key={r.p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: '0 0 45%', fontWeight: 600 }}>{r.p.name}</span>
                {r.options.length === 1 ? (
                  <span style={{ color: 'var(--ink-2)' }}>{r.options[0].label}</span>
                ) : (
                  <select
                    value={r.choice}
                    disabled={busy}
                    onChange={(e) => onChange(idx, e.target.value)}
                    style={{ flex: 1 }}>
                    <option value="" disabled>Choose condition…</option>
                    {r.options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {ATTEST_VERBAL_EACH} Attests each selected condition is expected to last at least
            3 months and places the patient at significant risk of hospitalization, acute
            exacerbation/decompensation, functional decline, or death.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || !allChosen} onClick={onConfirm}>
            {busy ? 'Enrolling…' : `Enroll ${n} in PCM`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReofferConsentModal({ patientName, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Re-offer consent"
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h3>Re-offer consent</h3></div>
        <div className="modal-body">
          <p>
            Moves <strong>{patientName}</strong> from declined back to consent pending, so
            consent can be captured again — verbally or with a consent link. The prior decline
            reason stays on the record. Reason is required and audit-logged.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{ width: '100%' }}
            placeholder="Patient reconsidered, new diagnosis discussed, family request, etc."
          />
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())}>
            Re-offer consent
          </button>
        </div>
      </div>
    </div>
  );
}

function DeclineModal({ count, busy, programFilter, bhiEnabled, onClose, onConfirm }) {
  const isAll = programFilter === 'all' && bhiEnabled;
  const initialMode = programFilter === 'bhi' ? 'bhi' : (isAll ? 'both' : 'ccm');
  const [mode, setMode] = useState(initialMode);
  const [reason, setReason] = useState('');
  const titleFor = (m) => m === 'bhi' ? 'Mark BHI declined' : (m === 'both' ? 'Mark declined (CCM + BHI)' : 'Mark consent declined');
  const buttonFor = (m) => m === 'bhi' ? 'Mark BHI declined' : (m === 'both' ? 'Mark both declined' : 'Mark declined');
  const bodyFor = (m) => {
    if (m === 'bhi') return `Marking ${count} patient${count !== 1 ? 's' : ''} as declined BHI. CCM consent is left untouched.`;
    if (m === 'both') return `Marking ${count} patient${count !== 1 ? 's' : ''} as declined for both CCM and BHI.`;
    return `Marking ${count} patient${count !== 1 ? 's' : ''} as declined consent.`;
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={titleFor(mode)}
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h3>{titleFor(mode)}</h3></div>
        <div className="modal-body">
          {isAll && (
            <div className="decline-mode-row" role="radiogroup" aria-label="Decline scope">
              <label><input type="radio" name="declineMode" value="ccm"  checked={mode === 'ccm'}  onChange={() => setMode('ccm')} /> CCM only</label>
              <label><input type="radio" name="declineMode" value="bhi"  checked={mode === 'bhi'}  onChange={() => setMode('bhi')} /> BHI only</label>
              <label><input type="radio" name="declineMode" value="both" checked={mode === 'both'} onChange={() => setMode('both')} /> Both</label>
            </div>
          )}
          <p>{bodyFor(mode)} Reason is required and audit-logged.</p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{ width: '100%' }}
            placeholder="Patient request, deceased, transferred care, etc."
          />
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason, mode)}>
            {buttonFor(mode)}
          </button>
        </div>
      </div>
    </div>
  );
}

function UnenrollModal({ count, busy, programSlot = 'ccm', onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const isBhi = programSlot === 'bhi';
  const title = isBhi ? 'Unenroll from BHI' : 'Unenroll from care management';
  const body = isBhi
    ? `Removing BHI (99484) enrollment for ${count} patient${count !== 1 ? 's' : ''}. CCM program and prior time logs stay on the record.`
    : `Removing the primary care-management program for ${count} patient${count !== 1 ? 's' : ''}. Consent and prior time logs stay on the record.`;
  const buttonLabel = isBhi ? 'Unenroll from BHI' : 'Unenroll';
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><h3>{title}</h3></div>
        <div className="modal-body">
          <p>{body}</p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{ width: '100%' }}
            placeholder="Reason for unenrollment"
          />
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn danger" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason)}>
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
